
import { isStreamingConfigEnabled } from "./integrations/stream_utils.js";
import {
    isTopLevelPrimitiveDateField,
    normalizeDateFilterValue,
    primitiveTimestampFallback,
} from "./filtering/dateFilterUtils.js";

const AWAITING_UPSTREAM_REASON = "awaiting_upstream";
function fastUnique(arr){
    return Array.from(new Set(arr));
}
const heatMapPalette = [
    {
        title: "Default",
        name:"default",
        category_colors:[
            '#1F77B4', '#FF7F0E', '#2CA02C', '#D62728', '#9467BD',
                                    '#8C564B', '#E377C2', '#7F7F7F', '#BCBD22', '#17BECF',
                                    '#AEC7E8', '#FFBB78', '#98DF8A', '#FF9896', '#C5B0D5'
        ],
        colors:[
            "#f7fcf0",
            "#e0f3db",
            "#ccebc5",
            "#a8ddb5",
            "#7bccc4",
            "#4eb3d3",
            "#2b8cbe",
            "#0868ac",
            "#084081"
        ]
    },{
        title: "Purple",
        name: "purple",
        colors:[
            "#003f5c",
            "#2f4b7c",
            "#665191",
            "#a05195",
            "#d45087",
            "#f95d6a",
            "#ff7c43",
            "#ffa600",
        ]
    },{
        title: "Purple 12",
        name: "purple_12",
        colors: ['#003f5c', '#20456b', '#394a78', '#514d83', '#6b508b', '#855192', '#e55878', '#f16468', '#f97356', '#fd8443', '#ff952c', '#ffa600']
        /*colors:[
            "#003f5c",
            "#2f4b7c",
            "#665191",
            "#a05195",
            "#d45087",
            "#f95d6a",
            "#ff7c43",
            "#ffa600",
        ]*/
    },{
        title: "Heat",
        name: "heat",
        colors:[
            "#f5f5ab",
            "#fed976",
            "#fc8c3c",
            "#f03b20",
            "#bd0026"
        ],text_colors:[
            "#222",
            "#222",
            "#222",
            "#f2f2f2",
            "#f2f2f2"
        ]
    },{
        title: "Sentiment 5",
        name: "sentiment_5",
        colors:[
            "#bd0026",
            "#fc8c3c",
            "#fed976",
            "#00bc7d",
            "#007a55"
        ],text_colors:[
            "#f2f2f2",
            "#f2f2f2",
            "#222",
            "#222",
            "#222",
            "#f2f2f2",
            "#f2f2f2"
        ]
    },{
        title: "Sentiment 7",
        name: "sentiment_7",
        colors:[
            "#bd0026",
            "#f03b20",
            "#fc8c3c",
            "#fed976",
            "#5ee9b5",
            "#00bc7d",
            "#007a55"
        ],text_colors:[
            "#f2f2f2",
            "#f2f2f2",
            "#222",
            "#222",
            "#222",
            "#f2f2f2",
            "#f2f2f2"
        ]
    },{
        title: "Green",
        name: "green",
        colors:[
            "#ecfdf5",
            "#a4f4cf",
            "#5ee9b5",
            "#00bc7d",
            "#007a55"
        ],text_colors:[
            "#222",
            "#222",
            "#222",
            "#f2f2f2",
            "#f2f2f2"
        ]
    },
    {
        title: "Ice Blue",
        name: "ice_blue",
        text_colors:[
            "#222",
            "#222",
            "#222",
            "#f2f2f2",
            "#f2f2f2"
        ],
        colors:[
            "#c6f1ff",
            "#89e2ff",
            "#46defc",
            "#077c97",
            "#025462"
        ]
    },
    {
        title: "CC Green",
        name: "cc_green",
        text_colors:[
            "#222",
            "#222",
            "#222",
            "#f2f2f2",
            "#f2f2f2"
        ],
        colors:[
            "#edfcf4",
            "#c5f5de",
            "#9defc9",
            "#75e8b3",
            "#4de29d",
            "#26db88",
            "#00d472",
            "#00d967"
          ]
    },
    {
        title: "Blue > yellow",
        name: "blue_yellow",
        text_colors:[
            "#f2f2f2",
            "#f2f2f2",
            "#222",
            "#222",
            "#222",
            "#222",
            "#222",
            "#222",
            "#222",
            "#222"
        ],
        colors: ['#00429d', '#2b57a7', '#426cb0', '#5681b9', '#6997c2', '#7daeca', '#93c4d2', '#abdad9', '#caefdf', '#ffffe0']
    },
    {
        title: "Dynamic heat",
        name: "dynamic_heat",
        dynamic: true,
        colors:[
            "#f5f5ab",
            "#bd0026"
        ]
    }
    

]


function isPromiseLike(value){
    return value && typeof value.then === "function";
}

const TRANSFORM_REGISTRY = new Map();

function registerTransform(name, config = {}){
    if(!name){
        throw new Error("Transform name is required");
    }
    TRANSFORM_REGISTRY.set(name, {
        helperKey: name,
        ...config,
    });
}

function getTransformDefinition(name){
    return name ? TRANSFORM_REGISTRY.get(name) : undefined;
}

function requireHelper(helpers, key, transformName){
    if(!helpers || typeof helpers[key] !== "function"){
        throw new Error(`Transform "${transformName}" requires helper "${key}"`);
    }
    return helpers[key];
}

function applyPinRelayPayload(entry, payload = {}){
    if(!payload){
        return;
    }
    if(entry.useConfig === "primitive"){
        entry.sources = payload.sources ?? [];
    }else if(entry.useConfig === "string"){
        if( payload.passThrough !== undefined ){
            entry.pass_through = payload.passThrough;
        }
        if( payload.passThroughConfig ){
            entry.passThroughCoonfig = payload.passThroughConfig;
        }
        if( payload.useConfigOverride ){
            entry.useConfig = payload.useConfigOverride;
        }
    }
}

registerTransform("source_data", {
    runSync(entry){
        entry.pass_through = entry.sourcePinConfig?.passThroughData;
    },
    runAsync: async (entry)=>{
        entry.pass_through = entry.sourcePinConfig?.passThroughData;
    }
});
registerTransform("anchored", {
    runAsync: async (entry, helpers = {})=>{
        const needsRelay = entry.sourcePrimitive && ["flow","flowinstance","flowcontrol"].includes(entry.sourcePrimitive.type)
        if( needsRelay ){
            const fn = requireHelper(helpers, "getPinRelayOutput", "anchored");
            const sources = await fn(entry);
            entry.sources = (Array.isArray(sources) && sources.length > 0) ? sources : entry.sourcePrimitive ? [entry.sourcePrimitive] : [];
        }else if(!Array.isArray(entry.sources) || entry.sources.length === 0){
            entry.sources = entry.sourcePrimitive ? [entry.sourcePrimitive] : [];
        }
    },
    runSync:(entry, helpers = {})=>{
        const needsRelay = entry.sourcePrimitive && ["flow","flowinstance","flowcontrol"].includes(entry.sourcePrimitive.type)
        if( needsRelay ){
            const fn = requireHelper(helpers, "getPinRelayOutput", "anchored");
            const sources = fn(entry);
            if(isPromiseLike(sources)){
                throw new Error(`Transform "anchored" helper returned a promise in sync mode`);
            }
            entry.sources = (Array.isArray(sources) && sources.length > 0) ? sources : entry.sourcePrimitive ? [entry.sourcePrimitive] : [];
        }else if(!Array.isArray(entry.sources) || entry.sources.length === 0){
            entry.sources = entry.sourcePrimitive ? [entry.sourcePrimitive] : [];
        }
    }
});
registerTransform("imports", {
    runAsync: async (entry, helpers)=>{
        const fn = requireHelper(helpers, "getImports", "imports");
        entry.sources = await fn(entry);
    },
    runSync:(entry, helpers)=>{
        const fn = requireHelper(helpers, "getImports", "imports");
        entry.sources = fn(entry);
    }
});
registerTransform("pin_relay", {
    runAsync: async (entry, helpers)=>{
        if( entry.useConfig === "primitive"){
            const fn = requireHelper(helpers, "getPinRelayOutput", "pin_relay");
            const sources = await fn(entry);
            entry.sources = sources ?? []
        }else{
            const fn = requireHelper(helpers, "getPinRelayString", "pin_relay");
            const payload = await fn(entry);
            entry.pass_through = payload?.value;
            entry.passThroughCoonfig = payload?.config ?? entry.passThroughCoonfig;
            if( payload?.useConfigOverride ){
                entry.useConfig = payload.useConfigOverride;
            }
        }
    },
    runSync:(entry, helpers)=>{
        if( entry.useConfig === "primitive"){
            const fn = requireHelper(helpers, "getPinRelayOutput", "pin_relay");
            const sources = fn(entry);
            if(isPromiseLike(sources)){
                throw new Error(`Transform "pin_relay" output helper returned a promise in sync mode`);
            }
            entry.sources = sources ?? []
        }else{
            const fn = requireHelper(helpers, "getPinRelayString", "pin_relay");
            const payload = fn(entry);
            if(isPromiseLike(payload)){
                throw new Error(`Transform "pin_relay" string helper returned a promise in sync mode`);
            }
            entry.pass_through = payload?.value;
            entry.passThroughCoonfig = payload?.config ?? entry.passThroughCoonfig;
            if( payload?.useConfigOverride ){
                entry.useConfig = payload.useConfigOverride;
            }
        }
    }
});
registerTransform("get_axis", {
    runAsync: async (entry, helpers)=>{
        const fn = requireHelper(helpers, "getAxisValues", "get_axis");
        const payload = await fn(entry);
        entry.pass_through = payload?.values ?? [];
        if( payload?.passThroughConfig ){
            entry.passThroughCoonfig = payload.passThroughConfig;
        }
    },
    runSync:(entry, helpers)=>{
        const fn = requireHelper(helpers, "getAxisValues", "get_axis");
        const payload = fn(entry);
        if(isPromiseLike(payload)){
            throw new Error(`Transform "get_axis" helper returned a promise in sync mode`);
        }
        entry.pass_through = payload?.values ?? [];
        if( payload?.passThroughConfig ){
            entry.passThroughCoonfig = payload.passThroughConfig;
        }
    }
});
registerTransform("child_list_to_string", {
    runAsync: async (entry, helpers)=>{
        const fn = requireHelper(helpers, "getChildList", "child_list_to_string");
        entry.sources = await fn(entry);
    },
    runSync:(entry, helpers)=>{
        const fn = requireHelper(helpers, "getChildList", "child_list_to_string");
        entry.sources = fn(entry);
    }
});
registerTransform("object_list_to_string", {
    runAsync: async ()=>{
        // handled during translateInputMap when sourcePinConfig advertises object_list,
        // so nothing to do during hydration.
    },
    runSync:()=>{
        // noop
    }
});
registerTransform("list_to_string", {
    runAsync: async ()=>{
        // translation handles conversion from string_list → string, so hydration is a no-op.
    },
    runSync:()=>{
        // noop
    }
});
registerTransform("flow_order", {
    runAsync: async (entry, helpers)=>{
        const fn = requireHelper(helpers, "getFlowOrder", "flow_order");
        const order = await fn(entry);
        entry.pass_through = order;
        entry.useConfig = "pass_through";
        entry.passThroughCoonfig = "string";
    },
    runSync:(entry, helpers)=>{
        const fn = requireHelper(helpers, "getFlowOrder", "flow_order");
        const order = fn(entry);
        if(isPromiseLike(order)){
            throw new Error(`Transform "flow_order" helper returned a promise in sync mode`);
        }
        entry.pass_through = order;
        entry.useConfig = "pass_through";
        entry.passThroughCoonfig = "string";
    }
});

async function runTransformAsync(entry, helpers){
    const transformName = entry?.sourceTransform;
    if(!transformName){
        return;
    }
    const definition = getTransformDefinition(transformName);
    if(!definition){
        throw new Error(`Unknown sourceTransform "${transformName}"`);
    }
    if(typeof definition.runAsync === "function"){
        await definition.runAsync(entry, helpers);
        return;
    }
    if(typeof definition.runSync === "function"){
        definition.runSync(entry, helpers);
    }
}

function runTransformSync(entry, helpers){
    const transformName = entry?.sourceTransform;
    if(!transformName){
        return;
    }
    const definition = getTransformDefinition(transformName);
    if(!definition){
        throw new Error(`Unknown sourceTransform "${transformName}"`);
    }
    if(typeof definition.runSync === "function"){
        const result = definition.runSync(entry, helpers);
        if(isPromiseLike(result)){
            throw new Error(`Transform "${entry.sourceTransform}" returned a promise in sync mode`);
        }
        return result;
    }
    if(typeof definition.runAsync === "function"){
        throw new Error(`Transform "${entry.sourceTransform}" does not support sync hydration`);
    }
}

const PrimitiveConfig = {
    "Constants":{
        LIVE_FILTER: 103,
        CONCEPT: 92,
        QUERY_RESULT: 82,
        VIEW: 38,
        GENERIC_SUMMARY: 109,
        TIMELINE_EVENT_SUMMARY: 178,
        DOC_DSL_QEURY: 181,
        DOC_DSL_SUMMARY: 182,
        EVALUATOR: 90,
        EVAL_CATEGORIZER: 144,
        SCORE: 120,
        SHAPE_ELEMENT: 145,
        IMAGE_ELEMENT: 172,
        TWITTER_POST: 149

    },
    "metadata":{
        "hypothesis": {
            icon: "LightBulbIcon",
            title: "Hypothesis"
        },
        "segment":{
            parameters:{
                "description":{
                    "title": "Description",
                    "description": "Description of segment",
                    "type":"long_string"
                },
            }
        },
        "category":{
            parameters:{
                "target":{
                    "title": "Categorize",
                    "description": "Items to categorize",
                    "type":"category_source"
                },
                "referenceId":{
                    "title": "Evidence",
                    "description": "Type of evidence",
                    "type":"categoryId",
                    "hidden": true
                },
                "field":{
                    "title": "Field",
                    "description": "Field of item to use",
                    "type":"category_field"
                },
                "theme":{
                    "title": "Categorization theme",
                    "description": "Categorization theme",
                    "optional": true,
                    "type":"string"
                }
            }
        },
        "element":{
            parameters:{
                "parentPin":{
                    "title":"Data source pin",
                    "description":"Select the page input this element should display.",
                    "type":"internal_parent_pin",
                    "order":5
                },
                "image_limit":{
                    "title":"Images to show",
                    "description":"Maximum number of images to render from connected pins (1-50).",
                    "type":"number",
                    "default":6,
                    "order":10
                },
                "image_descend":{
                    "title":"Include descendants",
                    "description":"Search descendant primitives for additional image sources.",
                    "type":"boolean",
                    "default":false,
                    "order":20
                },
                "image_referenceIds":{
                    "title":"Limit source categories",
                    "description":"Only pull images from primitives with these reference categories.",
                    "type":"categoryId",
                    "multi":true,
                    "order":30
                },
                "columns":{
                    "title":"Text columns",
                    "description":"Render summary/content text across multiple columns.",
                    "type":"number",
                    "default":1,
                    "order":31
                },
                "column_spacing":{
                    "title":"Column spacing",
                    "description":"Spacing between text columns in pixels.",
                    "type":"number",
                    "default":8,
                    "order":31.5
                },
                "image_spacing":{
                    "title":"Image spacing",
                    "description":"Space between image tiles (pixels).",
                    "type":"number",
                    "default":12,
                    "order":32
                },
                "image_padding":{
                    "title":"Gallery padding",
                    "description":"Padding around the gallery as a single number or comma-separated values (top,right,bottom,left).",
                    "type":"string",
                    "default":"",
                    "order":33
                },
                "image_showCaptions":{
                    "title":"Show captions",
                    "description":"Display primitive titles under each image tile.",
                    "type":"boolean",
                    "default":true,
                    "order":35
                },
                "image_startIndex":{
                    "title":"Starting image index",
                    "description":"Skip this many images before displaying the gallery.",
                    "type":"number",
                    "default":0,
                    "order":40
                },
                "image_fitMode":{
                    "title":"Image fit",
                    "description":"How gallery images should scale within their tiles.",
                    "type":"option_list",
                    "default":"scale",
                    "options":[
                        {"id":"scale","title":"Scale to fit"},
                        {"id":"center","title":"Center"},
                        {"id":"zoom","title":"Zoom to fill"}
                    ],
                    "order":50
                }
            }
        }
    },
    typeConfig:{
        "prompt": {
            allowedParents:["question"],
            needParent:true,
            needCategory:true,
            defaultTitle:false
        },
        "evidence":{
          //  embed: ["title", "quote"]
        },
        "result":{
            defaultReferenceId: 78,
            //embed: ["title"]
        },
        "observation":{
            needParent:true,
            allowedParents:["view","query"],
            defaultReferenceId: 167
        },
        "entity":{
          //  embed: ["title","referenceParameters.capabilities","referenceParameters.customers","referenceParameters.offerings"]
        },
        "activity": {
            needCategory:true,
            "createAtWorkspace": true,
        },
        "summary": {
            defaultReferenceId: 109,
        },
        "category": {
            needCategory:false,
            defaultReferenceId: 54,
            defaultTitle:false
        },
        "hypothesis": {
            needCategory:false,
            defaultReferenceId: 39
        },
        "page": {
            needCategory:true,
            defaultReferenceId: 140
        },
        "flow": {
            needCategory:true,
            defaultReferenceId: 130
        },
        "flowcontrol": {
            needCategory:true,
            defaultReferenceId: 160,
            defaultTitle:false
        },
        "view": {
            needParent:true,
            needCategory:false,
            defaultReferenceId: 38
        },
        "assessment": {
            allowedParents: ["venture"],
            needParent:true,
            needCategory:true,
        },
        "search": {
            needParent:true,
            needCategory:true,
            defaultTitle:false,
            defaults:{
                useTerms: true
            },
            render:{
                background: "#059669",
                accentBackground: "#3f6212"
            }
        },
        "element": {
            needCategory:true,
            defaultReferenceId: 89
        },
        "concept": {
            needCategory:true,
            defaultReferenceId: 92
        },
        "board": {
            needCategory:true,
            defaultReferenceId: 102
        },
        "actionrunner": {
            needCategory:true,
            defaultReferenceId: 131
        },
        "chat": {
            needParent: true,
            needCategory:false,
            defaultTitle: false
        },
        "plan": {
            needParent: true,
            needCategory: false,
            defaultTitle: false,
        },
        "step": {
            needParent: true,
            needCategory: false,
            defaultTitle: false,
        },
        "external": {
            needCategory:false,
            defaultTitle:false,
            render:{
                background: "#a21caf",
                accentBackground: "#3f6212"
            }
        },
        "workstream": {
            needParent: true,
            needCategory: false,
            defaultTitle: false,
        },
    },
    types: ["hypothesis", 
        "learning",
        "activity",
        "result",
        "observation",
        "experiment",
        "question", 
        "evidence", 
        "prompt",
        "venture",
        "assessment", 
        "entity", 
        "category", 
        "segment", 
        "view", 
        "search",
        "detail",
        "query", 
        "report", 
        "element", 
        "reportinstance", 
        "concept", 
        "board", 
        "marketsegment", 
        "working", 
        "summary",
        "actionrunner",
        "flow",
        "flowcontrol",
        "flowinstance",
        "categorizer",
        "action",
        "page",
        "chat",
        "plan",
        "step",
        "external",
        "workstream"
    ],
    pageview:{
        "board":{
            defaultWide: "board" 
        },
        "report":{
            defaultWide: "report" 
        },
        "segment":{
         //   evidence: false,
          //  viewer: true
          //  defaultWide: {type: 'result', index: 0} 
        }
    },
    sidebar:{
        "segment":{
            source: false,
            addToResult: "segment",
            addToItems: [42],
        },
        "evidence":{
            showRefs: true
        },
        "summary":{
            showRefs: true
        }
    },
    stateInfo:{
        "hypothesis":{
            "open": {title: "Open", colorBase: "blue"},
            "invalid": {title: "Invalidated", colorBase: "amber"},
            "valid": {title: "Validated", colorBase: "cyan"},
        },
        "activity":{
            "open": {title: "Not started", colorBase: "blue"},
            "active": {title: "Underway", colorBase: "amber"},
            "closed": {title: "Completed", colorBase: "green"},
        },
        "experiment":{
            "open": {title: "Not started", colorBase: "blue"},
            "active": {title: "Underway", colorBase: "amber"},
            "closed": {title: "Completed", colorBase: "green"},
        },
        default: {
            "open": {title: "Open"},
            "closed": {title: "Closed"},
        }
    },
    passTypeMap: {
        "type": "origin_type",
        "options": "raw",
        "title": "raw",
        "pin": "pin",
        "primitive": "primitive",
        "revenue": "currency",
        "currency": "currency",
        "marketCap": "currency",
        "previousClose": "currency",
        "fiftyDayAverage": "currency",
        "valuation": "funding",
        "employee_count": "number",
        "funding": "funding",
        "contact": "contact",
        "icon": "icon",
        "boolean": "boolean",
        "question": "question",
        "segment_filter":"segment_filter",
        "metric_fields": "metric",
        "createdAt": "date",
        "updatedAt": "date",
        "structured_summary": "structured_summary"
    },
    heatMapPalette:heatMapPalette,
    renderConfigs:{
            default: {title:"Show items",id:0, parameters: {showAsCounts:false},
                config:{
                    "columns":{
                        type: "column_count",
                        title: "Columns per cell",
                        allow_auto: true,
                        rebalance_width: true,
                        default: 1,
                        max: 20
                    },
                    "show_row_headers":{
                        type: "boolean",
                        title: "Row headers",
                        default: true,
                    },"show_column_headers":{
                        type: "boolean",
                        title: "Column headers",
                        default: true,
                    },"columnAsIcon":{
                        type: "boolean",
                        title: "Column Icons",
                        default: true,
                    },"rowAsIcon":{
                        type: "boolean",
                        title: "Row Icons",
                        default: true,
                    },
                    "axis_text":{
                        type: "axis_text",
                        title: "Headers"
                    },
                    "summary":{
                        type: "option_list",
                        title: "Show breakdown",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    }

                }},
                social: {title:"Show content",id:13,parameters: {showAsCounts:false},
                config:{
                    "columns":{
                        type: "column_count",
                        title: "Text length",
                        default: 1,
                        max: 20
                    },
                    "text_length":{
                        type: "option_list",
                        title: "Text length",
                        default: 60,
                        options: [
                            {id:20, title: "Short"},
                            {id:60, title: "Medium"},
                            {id:150, title: "Long"}
                        ]
                    },"extract_hashtags":{
                        type: "option_list",
                        title: "Extract hastags",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:5, title: "Up to 5"},
                            {id:10, title: "Up to 10"},
                            {id:true, title: "All"}
                        ]
                    }

                }},
            checktable: {id: 2,title:"Truth table", matrixType: "checktable", renderType: "checktable",parameters: {},
                config:{

                    "max_cols":{
                        type: "option_list",
                        title: "Max columns",
                        default: undefined,
                        options: [
                            {id: undefined, title: "No cap"},
                            {id: 5, title: "5"},
                            {id: 10, title: "10"},
                            {id: 15, title: "15"},
                            {id: 20, title: "20"},
                            {id: 25, title: "25"},
                            {id: 30, title: "30"},
                        ]
                    }
                
            }},
            score_dial: {id: 3,title:"Score dial", renderType: "dials",parameters: {},
                config:{
                    "colors":{
                        type: "option_list",
                        title: "Colors",
                        default: "green",
                        options: [
                            {id:"green", title: "Green"},
                            {id:"blue", title: "Blue"}
                        ]
                    },
                }
            },
            counts: {id:1, title:"Heatmap", "matrixType": "heatmap",
                config:{
                    "colors":{
                        type: "option_list",
                        title: "Colors",
                        default: "default",
                        options: heatMapPalette.map(d=>({id: d.name, title:d.title}))
                    },
                    "group_by": {
                        type: "option_list",
                        title:"Range source",
                        default: "all",
                        options: [{
                            id: "all",
                            title: "All data"
                        },{
                            id: "row",
                            title: "Rows"
                        },{
                            id: "col",
                            title: "Columns"
                        }]
                    },
                    "counts":{
                        type: "option_list",
                        title: "Show count",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:"number", title: "Number"},
                            {id:"percentage", title: "Percentage"}
                        ]
                    },
                    "axis_text":{
                        type: "axis_text",
                        title: "Headers"
                    },
                    "titles":{
                        type: "boolean",
                        title: "Show title",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_legend":{
                        type: "option_list",
                        title: "Show Legend",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "bubble":{
                        type: "boolean",
                        title: "Show as bubbles",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_row_headers":{
                        type: "boolean",
                        title: "Row headers",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_column_headers":{
                        type: "boolean",
                        title: "Column headers",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },"columnAsIcon":{
                        type: "boolean",
                        title: "Column Icons",
                        default: true,
                    },"rowAsIcon":{
                        type: "boolean",
                        title: "Row Icons",
                        default: true,
                    },
                    "show_row_totals":{
                        type: "boolean",
                        title: "Row totals",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_column_totals":{
                        type: "boolean",
                        title: "Column totals",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    }
                },
                showAsCounts:true
            },
            cat_overview: {id: 4,title:"Category overview", renderType: "cat_overview",parameters: {},
                config:{
                    "style":{
                        type: "option_list",
                        title: "Chart Type",
                        default: true,
                        options: [
                            {id:"bar", title: "Bar graph"},
                            {id:"pie", title: "Pie chart"},
                        ]
                    },
                    "show_none":{
                        type: "option_list",
                        title: "Show None",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_title":{
                        type: "option_list",
                        title: "Show Title",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "by_tag":{
                        type: "option_list",
                        title: "Color by tag",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_legend":{
                        type: "option_list",
                        title: "Show Legend",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_x_axis":{
                        type: "option_list",
                        title: "Show X Axis",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_y_axis":{
                        type: "option_list",
                        title: "Show Y Axis",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "secondary_axis_icons":{
                        type: "boolean",
                        title: "Secondary axis icons",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "secondary_axis_icon_scale":{
                        type: "option_list",
                        title: "Icon scale",
                        default: 1,
                        options: [
                            {id:0.75, title: "Small"},
                            {id:1, title: "Default"},
                            {id:1.25, title: "Large"},
                            {id:1.5, title: "X-Large"}
                        ]
                    },
                }},
            field: {id: 5,title:"Field of item", renderType: "field",parameters: {},config:{}},
            detail_grid: {id: 6, title:"Details", renderType: "overview",parameters: {},config:{}},
            word_cloud: {id: 7,title:"Word cloud", renderType: "cat_overview", configName:"word_cloud", parameters: {},
                config:{
                    "show_none":{
                        type: "option_list",
                        title: "Show None",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_title":{
                        type: "option_list",
                        title: "Show Title",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "by_tag":{
                        type: "option_list",
                        title: "Color by tag",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_legend":{
                        type: "option_list",
                        title: "Show Legend",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "secondary_axis_icons":{
                        type: "boolean",
                        title: "Secondary axis icons",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "secondary_axis_icon_scale":{
                        type: "option_list",
                        title: "Icon scale",
                        default: 1,
                        options: [
                            {id:0.75, title: "Small"},
                            {id:1, title: "Default"},
                            {id:1.25, title: "Large"},
                            {id:1.5, title: "X-Large"}
                        ]
                    },
                }},
            chart: {id: 8,title:"Distribution chart", renderType: "chart", configName: "chart", parameters: {},
                config:{
                    "colors":{
                        type: "option_list",
                        title: "Colors",
                        default: "default",
                        options: heatMapPalette.map(d=>({id: d.name, title:d.title}))
                    },
                    "show_title":{
                        type: "option_list",
                        title: "Show Title",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "style":{
                        type: "option_list",
                        title: "Style",
                        default: "pie",
                        options: [
                            {id:"bar", title: "Bar"},
                            {id:"pie", title: "Pie"},
                            {id:"dial", title: "Dial"}
                        ]
                    },
                    "show_legend":{
                        type: "option_list",
                        title: "Show Legend",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_x_axis":{
                        type: "option_list",
                        title: "Show X Axis",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_y_axis":{
                        type: "option_list",
                        title: "Show Y Axis",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                }},
            dial: {id: 9,title:"Dial", renderType: "dial", configName: "dial", parameters: {},
                config:{
                    "invert":{
                        type: "option_list",
                        title: "Invert",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_label":{
                        type: "option_list",
                        title: "Show label",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_number":{
                        type: "option_list",
                        title: "Show number",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_title":{
                        type: "option_list",
                        title: "Show Title",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    }
                }},
            subchart: {id: 11,title:"Sub distribution chart", "matrixType": "distribution", renderType: "grid", configName: "subchart", parameters: {},
                needsAllAllocations: true,
                config:{
                    "colors":{
                        type: "option_list",
                        title: "Colors",
                        default: "default",
                        options: heatMapPalette.map(d=>({id: d.name, title:d.title}))
                    },
                    "show_title":{
                        type: "boolean",
                        title: "Show Title",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "style":{
                        type: "option_list",
                        title: "Style",
                        default: "pie",
                        options: [
                            {id:"bar", title: "Bar"},
                            {id:"stacked_bar", title: "Stacked Bar"},
                            {id:"pie", title: "Pie"},
                            {id:"dial", title: "Dial"},
                            {id:"weighted", title: "Weighted average"},
                        ]
                    },
                    "show_value":{
                        type: "option_list",
                        title: "Show Value",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:"number", title: "Number"},
                            {id:"percent", title: "Percent"}
                        ]
                    },
                    "show_x_axis":{
                        type: "option_list",
                        title: "Show X Axis",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_y_axis":{
                        type: "option_list",
                        title: "Show Y Axis",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "secondary_axis_icons":{
                        type: "boolean",
                        title: "Secondary axis icons",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "secondary_axis_icon_scale":{
                        type: "option_list",
                        title: "Icon scale",
                        default: 1,
                        options: [
                            {id:0.75, title: "Small"},
                            {id:1, title: "Default"},
                            {id:1.25, title: "Large"},
                            {id:1.5, title: "X-Large"}
                        ]
                    },
                    "reverse_palette":{
                        type: "boolean",
                        title: "Reverse Palette",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "order":{
                        type: "option_list",
                        title: "Order by",
                        default: false,
                        options: [
                            {id:false, title: "None"},
                            {id:"labels", title: "Label"},
                            {id:"high_to_low", title: "Count (High to low)"},
                            {id:"low_to_high", title: "Count (Low to high)"}
                        ]
                    },
                    "calcRange":{
                        type: "option_list",
                        title: "Relative scale",
                        default: false,
                        options: [
                            {id:false, title: "None"},
                            {id:true, title: "by chart"},
                            {id:"column", title: "by column"},
                            {id:"row", title: "by row"}
                        ]
                    },
                    "axis_text":{
                        type: "axis_text",
                        title: "Headers"
                    },
                    "legend_size":{
                        type: "font_size",
                        title: "Legend size",
                        default: true
                    },
                    "show_legend":{
                        type: "option_list",
                        title: "Show Legend",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Below"},
                            {id:"right", title: "On right"},
                            {id:"each-below", title: "Below each"},
                            {id:"each-right", title: "Right of each"}
                        ]
                    },
                    "show_row_headers":{
                        type: "boolean",
                        title: "Row headers",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_column_headers":{
                        type: "boolean",
                        title: "Column headers",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "columnAsIcon":{
                        type: "boolean",
                        title: "Column Icons",
                        default: true,
                    },"rowAsIcon":{
                        type: "boolean",
                        title: "Row Icons",
                        default: true,
                    },
                    "show_row_totals":{
                        type: "boolean",
                        title: "Row totals",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_column_totals":{
                        type: "boolean",
                        title: "Column totals",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    }
                }},
            overUnderChart: {id: 12,title:"Over / under chart", "matrixType": "overunder", renderType: "grid", configName: "overUnderChart", parameters: {},
                needsAllAllocations: true,
                config:{
                    "colors":{
                        type: "option_list",
                        title: "Colors",
                        default: "default",
                        options: heatMapPalette.map(d=>({id: d.name, title:d.title}))
                    },
                    "show_title":{
                        type: "option_list",
                        title: "Show Title",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "center":{
                        type: "option_list",
                        title: "Center Axis",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_legend":{
                        type: "option_list",
                        title: "Show Legend",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "rowAsIcon":{
                        type: "boolean",
                        title: "Row Icons",
                        default: true,
                    },                    
                    "bar_color_by_score":{
                        type: "option_list",
                        title: "Color by score",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_breakdown":{
                        type: "option_list",
                        title: "Show Pie",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                }},
                xycluster: {id: 18,title:"X/Y cluster", "matrixType": "xy_cluster", renderType: "grid", configName: "xycluster", parameters: {},
                needsAllAllocations: true,
                config:{
                    "colors":{
                        type: "option_list",
                        title: "Colors",
                        default: "default",
                        options: heatMapPalette.map(d=>({id: d.name, title:d.title}))
                    },
                    "show_title":{
                        type: "option_list",
                        title: "Show Title",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "label_icons":{
                        type: "boolean",
                        title: "Show labels as icons",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "column_is_discrete":{
                        type: "boolean",
                        title: "Column axis is discrete",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "row_is_discrete":{
                        type: "boolean",
                        title: "Row axis is discrete",
                        default: false,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "label_connectors":{
                        type: "boolean",
                        title: "Show label connectors",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "show_labels":{
                        type: "boolean",
                        title: "Show labels",
                        default: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                }},
                social_timeseries: {id: 21,title:"Social timeseries", "matrixType": "timeseries", renderType: "grid", configName: "social_timeseries", parameters: {},
                needsAllAllocations: true,
                "dataConfig": {
                    "timeseries": true
                },
                config:{
                        "cumulative": {
                        "type": "option_list",
                        "title": "Cumulative",
                        "default": false,
                        "options": [
                            {
                            "id": false,
                            "title": "No"
                            },
                            {
                            "id": true,
                            "title": "Yes"
                            }
                        ]
                        },
                        "range": {
                        "type": "option_list",
                        "title": "Range",
                        "default": "12",
                        "options": [
                            {
                            "id": null,
                            "title": "None"
                            },
                            {
                            "id": "1mo",
                            "title": "4 weeks"
                            },
                            {
                            "id": "3mo",
                            "title": "3 months"
                            },
                            {
                            "id": "6mo",
                            "title": "6 months"
                            },
                            {
                            "id": "1",
                            "title": "1 year"
                            },
                            {
                            "id": "2",
                            "title": "2 year"
                            },
                            {
                            "id": "5",
                            "title": "5 year"
                            },
                            {
                            "id": "10",
                            "title": "10 year"
                            }
                        ]
                        },
                        "timeseries_style": {
                        "type": "option_list",
                        "title": "Chart style",
                        "default": "line",
                        "options": [
                            {
                            "id": "line",
                            "title": "Line"
                            },
                            {
                            "id": "bar",
                            "title": "Bar"
                            }
                        ]
                        },
                        "timeseries_palette": {
                        "type": "option_list",
                        "title": "Palette",
                        "default": "default",
                        "options": [
                            ...heatMapPalette.map(d=>({id: d.name, title: d.title})),
                            {id: "default", title: "Default categorical"},
                            {id: "pastel", title: "Pastel"},
                            {id: "muted", title: "Muted"},
                            {id: "std", title: "Standard"},
                            {id: "classic", title: "Classic"},
                            {id: "vibrant", title: "Vibrant"}
                        ]
                        },
                        "timeseries_anchor": {
                        "type": "option_list",
                        "title": "Range anchor",
                        "default": "now",
                        "options": [
                            {
                            "id": "now",
                            "title": "Today"
                            },
                            {
                            "id": "latest_data",
                            "title": "Latest data point"
                            }
                        ]
                        },
                        "timeseries_color_by": {
                        "type": "option_list",
                        "title": "Color by",
                        "default": "row",
                        "options": [
                            {
                            "id": "row",
                            "title": "Row"
                            },
                            {
                            "id": "column",
                            "title": "Column"
                            },
                            {
                            "id": "series",
                            "title": "Series"
                            },
                            {
                            "id": "allocation",
                            "title": "Allocation"
                            },
                            {
                            "id": "cell",
                            "title": "Per cell"
                            }
                        ]
                        },
                        "timeseries_linked_scale": {
                        "type": "boolean",
                        "title": "Link Y scale across series",
                        "default": true,
                        "options": [
                            {
                            "id": true,
                            "title": "Linked"
                            },
                            {
                            "id": false,
                            "title": "Independent"
                            }
                        ]
                        },
                        "timeseries_show_value_label": {
                        "type": "boolean",
                        "title": "Show Y-axis value label",
                        "default": true,
                        "options": [
                            {
                            "id": true,
                            "title": "Show"
                            },
                            {
                            "id": false,
                            "title": "Hide"
                            }
                        ]
                        },
                        "timeseries_show_x_axis": {
                        "type": "boolean",
                        "title": "Show X axis",
                        "default": true,
                        "options": [
                            {
                            "id": true,
                            "title": "Show"
                            },
                            {
                            "id": false,
                            "title": "Hide"
                            }
                        ]
                        },
                        "timeseries_x_label_format": {
                        "type": "option_list",
                        "title": "X label format",
                        "default": "month_short",
                        "options": [
                            {
                            "id": "full",
                            "title": "Full date"
                            },
                            {
                            "id": "month_short",
                            "title": "Month (short)"
                            },
                            {
                            "id": "month_long",
                            "title": "Month (long)"
                            },
                            {
                            "id": "year_short",
                            "title": "Year (short)"
                            },
                            {
                            "id": "year_long",
                            "title": "Year (long)"
                            }
                        ]
                        },
                        "timeseries_x_axis_label": {
                        "type": "string",
                        "title": "X axis label",
                        "default": ""
                        },
                        "timeseries_show_y_axis": {
                        "type": "boolean",
                        "title": "Show Y axis",
                        "default": false,
                        "options": [
                            {
                            "id": true,
                            "title": "Show"
                            },
                            {
                            "id": false,
                            "title": "Hide"
                            }
                        ]
                        },
                        "timeseries_y_axis_label": {
                        "type": "string",
                        "title": "Y axis label",
                        "default": ""
                        },
                        "timeseries_show_average": {
                        "type": "boolean",
                        "title": "Show average",
                        "default": false,
                        "options": [
                            {
                            "id": true,
                            "title": "Show"
                            },
                            {
                            "id": false,
                            "title": "Hide"
                            }
                        ]
                        },
                        "timeseries_show_trend": {
                        "type": "boolean",
                        "title": "Show trend",
                        "default": false,
                        "options": [
                            {
                            "id": true,
                            "title": "Show"
                            },
                            {
                            "id": false,
                            "title": "Hide"
                            }
                        ]
                        },
                        "legend_size":{
                        "type": "font_size",
                        "title": "Legend size",
                        "default": true
                        },
                        "show_legend":{
                        "type": "option_list",
                        "title": "Show Legend",
                        "default": false,
                        "options": [
                            {id:false, title: "No"},
                            {id:true, title: "Below"},
                            {id:"right", title: "On right"}
                        ]
                        },
                        "resolution": {
                        "type": "option_list",
                        "title": "Resolution",
                        "default": "day",
                        "options": [
                            {
                            "id": "second",
                            "title": "Second"
                            },
                            {
                            "id": "minute",
                            "title": "Minute"
                            },
                            {
                            "id": "hour",
                            "title": "Hour"
                            },
                            {
                            "id": "day",
                            "title": "Day"
                            },
                            {
                            "id": "week",
                            "title": "Week"
                            },
                            {
                            "id": "month",
                            "title": "Month"
                            },
                            {
                            "id": "year",
                            "title": "Year"
                            }
                        ]
                        },
                        "cap": {
                        "type": "option_list",
                        "title": "Max delta",
                        "default": false,
                        "options": [
                            {
                            "id": false,
                            "title": "None"
                            },
                            {
                            "id": 7,
                            "title": "7 ticks"
                            },
                            {
                            "id": 8,
                            "title": "8 ticks"
                            },
                            {
                            "id": 12,
                            "title": "12 ticks"
                            },
                            {
                            "id": 14,
                            "title": "14 ticks"
                            },
                            {
                            "id": 21,
                            "title": "21 ticks"
                            },
                            {
                            "id": 24,
                            "title": "24 ticks"
                            },
                            {
                            "id": 28,
                            "title": "28 ticks"
                            },
                            {
                            "id": 56,
                            "title": "28 ticks"
                            },
                            {
                            "id": 112,
                            "title": "28 ticks"
                            }
                        ]
                        },
                        "calcRange": {
                        "type": "option_list",
                        "title": "Relative scale",
                        "default": false,
                        "options": [
                            {
                            "id": false,
                            "title": "None"
                            },
                            {
                            "id": true,
                            "title": "by chart"
                            },
                            {
                            "id": "column",
                            "title": "by column"
                            },
                            {
                            "id": "row",
                            "title": "by row"
                            }
                        ]
                        },
                        "timeRange": {
                        "type": "option_list",
                        "title": "Time axis",
                        "default": false,
                        "options": [
                            {
                            "id": false,
                            "title": "None"
                            },
                            {
                            "id": "delta_row",
                            "title": "Delta (row)"
                            },
                            {
                            "id": "delta_column",
                            "title": "Delta (column)"
                            },
                            {
                            "id": "column",
                            "title": "align column"
                            },
                            {
                            "id": "row",
                            "title": "align row"
                            }
                        ]
                        },
                        "show_row_headers": {
                        "type": "option_list",
                        "title": "Row heaaders",
                        "default": true,
                        "options": [
                            {
                            "id": false,
                            "title": "No"
                            },
                            {
                            "id": true,
                            "title": "Yes"
                            }
                        ]
                        },
                        "show_column_headers": {
                        "type": "option_list",
                        "title": "Column heaaders",
                        "default": true,
                        "options": [
                            {
                            "id": false,
                            "title": "No"
                            },
                            {
                            "id": true,
                            "title": "Yes"
                            }
                        ]
                        }
                }
            },
                series_chart: {id: 19,title:"Series chart", "matrixType": "series", renderType: "grid", configName: "series_chart", parameters: {},
                needsAllAllocations: true,
                config:{
                    "colors":{
                        type: "option_list",
                        title: "Colors",
                        default: "default",
                        options: heatMapPalette.map(d=>({id: d.name, title:d.title}))
                    },
                    "series_style":{
                        type: "option_list",
                        title: "Style",
                        default: "line",
                        options: [
                            {id:"line", title: "Straight line"},
                            {id:"curved", title: "Curved line"},
                            {id:"bar", title: "Bar"}
                        ]
                    },
                    "series_scale":{
                        type: "option_list",
                        title: "Scale mode",
                        default: "global",
                        options: [
                            {id:"global", title: "Sync across series"},
                            {id:"per_series", title: "Per series range"}
                        ]
                    },
                    "series_group_mode":{
                        type: "option_list",
                        title: "Series display",
                        default: "per_series",
                        options: [
                            {id:"per_series", title: "Each allocation as line"},
                            {id:"average", title: "Average across allocations"}
                        ]
                    },
                    "show_axis_markers":{
                        type: "boolean",
                        title: "Axis markers",
                        default: true,
                        options: [
                            {id:false, title: "Hide"},
                            {id:true, title: "Show"}
                        ]
                    },
                    "show_axis_labels":{
                        type: "boolean",
                        title: "Axis labels",
                        default: true,
                        options: [
                            {id:false, title: "Hide"},
                            {id:true, title: "Show"}
                        ]
                    },
                    "axis_text":{
                        type: "axis_text",
                        title: "Label size",
                        default: 10
                    },
                    "axis_text_size":{
                        type: "font_size",
                        title: "Axis label size",
                        default: 10
                    },
                    "value_text_size":{
                        type: "font_size",
                        title: "Value label size",
                        default: 9
                    },
                    "show_values":{
                        type: "boolean",
                        title: "Show values",
                        default: false,
                        options: [
                            {id:false, title: "Hide"},
                            {id:true, title: "Show"}
                        ]
                    },
                    "columnAsIcon":{
                        type: "boolean",
                        title: "Column icons",
                        default: false,
                        options: [
                            {id:false, title: "Text"},
                            {id:true, title: "Icons"}
                        ]
                    },
                    "rowAsIcon":{
                        type: "boolean",
                        title: "Row icons",
                        default: false,
                        options: [
                            {id:false, title: "Text"},
                            {id:true, title: "Icons"}
                        ]
                    }
                }},
                market_map_summary: {
                    id: "market_map_109_view",
                    title: "Market map",
                    renderType: "market_map",
                    resizable: true,
                    resizeMode: "plain",
                    parameters: {},
                    config: {
                        "market_map_x_axis": {
                            type: "option_list",
                            title: "X axis",
                            default: "axis_1",
                            options: [
                                { id: "axis_1", title: "Axis 1" },
                                { id: "axis_2", title: "Axis 2" },
                                { id: "axis_3", title: "Axis 3" }
                            ]
                        },
                        "market_map_y_axis": {
                            type: "option_list",
                            title: "Y axis",
                            default: "axis_2",
                            options: [
                                { id: "axis_1", title: "Axis 1" },
                                { id: "axis_2", title: "Axis 2" },
                                { id: "axis_3", title: "Axis 3" }
                            ]
                        },
                        "market_map_show_axis_titles": {
                            type: "boolean",
                            title: "Axis titles",
                            default: true,
                            options: [
                                {id:false, title: "Hide"},
                                {id:true, title: "Show"}
                            ]
                        },
                        "market_map_axis_extreme_font_size": {
                            type: "font_size",
                            title: "Extreme label size",
                            default: 30,
                            min: 12,
                            max: 64
                        },
                        "market_map_show_grid": {
                            type: "boolean",
                            title: "Grid lines",
                            default: false,
                            options: [
                                {id:false, title: "Hide"},
                                {id:true, title: "Show"}
                            ]
                        },
                        "market_map_show_bucket_values": {
                            type: "boolean",
                            title: "Bucket values",
                            default: false,
                            options: [
                                {id:false, title: "Hide"},
                                {id:true, title: "Show"}
                            ]
                        },
                        "market_map_bucket_font_size": {
                            type: "font_size",
                            title: "Bucket label size",
                            default: 10,
                            min: 6,
                            max: 24
                        },
                        "market_map_display_mode": {
                            type: "option_list",
                            title: "Companies",
                            default: "markers",
                            options: [
                                { id: "markers", title: "Markers and labels" },
                                { id: "images", title: "Company images" }
                            ]
                        },
                        "market_map_palette": {
                            type: "option_list",
                            title: "Palette",
                            default: "default",
                            options: [
                                { id: "theme", title: "Theme categorical" },
                                ...heatMapPalette.map(d=>({id: d.name, title: d.title})),
                                { id: "default", title: "Default categorical" },
                                { id: "pastel", title: "Pastel" },
                                { id: "muted", title: "Muted" },
                                { id: "std", title: "Standard" },
                                { id: "classic", title: "Classic" },
                                { id: "vibrant", title: "Vibrant" }
                            ]
                        },
                        "market_map_reverse_palette": {
                            type: "boolean",
                            title: "Reverse palette",
                            default: false,
                            options: [
                                {id:false, title: "No"},
                                {id:true, title: "Yes"}
                            ]
                        }
                    }
                },
                timeline_event_summary: {
                id: "timeline_178_view",
                title: "Event timeline",
                renderType: "full",
                resizable: true,
                resizeMode: "plain",
                parameters: {},
                config: {
                    "timeline_orientation": {
                        type: "option_list",
                        title: "Orientation",
                        default: "horizontal",
                        relay: true,
                        options: [
                            { id: "horizontal", title: "Horizontal" },
                            { id: "vertical", title: "Vertical" }
                        ]
                    },
                    "timeline_layers": {
                        type: "option_list",
                        title: "Label layers",
                        default: 2,
                        relay: true,
                        options: [
                            { id: 1, title: "1 layer" },
                            { id: 2, title: "2 layers" },
                            { id: 3, title: "3 layers" },
                            { id: 4, title: "4 layers" },
                            { id: 5, title: "5 layers" }
                        ]
                    },
                    "timeline_font_size": {
                        type: "font_size",
                        title: "Font size",
                        default: 12,
                        relay: true,
                        min: 8,
                        max: 28
                    },
                    "timeline_gap": {
                        type: "option_list",
                        title: "Line spacing",
                        default: 18,
                        relay: true,
                        options: [
                            { id: 10, title: "Compact" },
                            { id: 18, title: "Default" },
                            { id: 28, title: "Spacious" },
                            { id: 36, title: "Wide" }
                        ]
                    },
                    "timeline_first_layer_offset": {
                        type: "option_list",
                        title: "First layer offset",
                        default: 22,
                        relay: true,
                        options: [
                            { id: 12, title: "Near" },
                            { id: 22, title: "Default" },
                            { id: 32, title: "Far" },
                            { id: 44, title: "Extra far" }
                        ]
                    },
                    "timeline_line_width": {
                        type: "option_list",
                        title: "Timeline thickness",
                        default: 3,
                        relay: true,
                        options: [
                            { id: 2, title: "Thin" },
                            { id: 3, title: "Default" },
                            { id: 5, title: "Thick" },
                            { id: 7, title: "Extra thick" }
                        ]
                    },
                    "timeline_markers": {
                        type: "option_list",
                        title: "Time markers",
                        default: "auto",
                        relay: true,
                        options: [
                            { id: "auto", title: "Auto" },
                            { id: "year", title: "Year" },
                            { id: "quarter", title: "Quarter" },
                            { id: "month", title: "Month" },
                            { id: "none", title: "None" }
                        ]
                    },
                    "timeline_show_label_dates": {
                        type: "boolean",
                        title: "Show label dates",
                        default: false,
                        relay: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "timeline_palette": {
                        type: "option_list",
                        title: "Palette",
                        default: "theme",
                        relay: true,
                        options: [
                            { id: "theme", title: "Theme categorical" },
                            ...heatMapPalette.map(d=>({id: d.name, title: d.title})),
                            { id: "default", title: "Default categorical" },
                            { id: "pastel", title: "Pastel" },
                            { id: "muted", title: "Muted" },
                            { id: "std", title: "Standard" },
                            { id: "classic", title: "Classic" },
                            { id: "vibrant", title: "Vibrant" }
                        ]
                    },
                    "timeline_reverse_palette": {
                        type: "boolean",
                        title: "Reverse palette",
                        default: false,
                        relay: true,
                        options: [
                            {id:false, title: "No"},
                            {id:true, title: "Yes"}
                        ]
                    },
                    "timeline_start": {
                        type: "string",
                        title: "Start date",
                        default: "",
                        relay: true,
                        placeholder: "YYYY-MM-DD"
                    },
                    "timeline_end": {
                        type: "string",
                        title: "End date",
                        default: "",
                        relay: true,
                        placeholder: "YYYY-MM-DD"
                    }
                }
            }


        },
    decodeParameter:(data, path)=>{
        if (!data || !path) return undefined;
        if (typeof(path) !== "string") return undefined;
        const parts = path.split(".");
        for (let i = 0; i < parts.length; i++) {
            if (!data) return undefined; 
            data = data[parts[i]];
        }
        return data;
    },
    decodeParameterFromParts:(data, parts)=>{
        if (!data || !parts) return undefined;
        for (let i = 0; i < parts.length; i++) {
            if (!data) return undefined; 
            data = data[parts[i]];
        }
        return data;
    },
    decodeExploreFilter:(filter)=>{
        if( !filter){
            return filter
        }
        if( typeof(filter) === "object" && !Array.isArray(filter) ){
            return filter
        }
        return filter.reduce((a,c)=>{
            if( c instanceof Object ){
                a[c.idx] = c
            }else{
                a[c === null ? "_N_" : c] = c
            }
            return a
        }, {}) 
    },
    encodeExploreFilter:(option, val, invert)=>{
        if( val === undefined || val.length === 0){
            return undefined
        }

        if( val instanceof Object && !Array.isArray(val)){
            if( val.bucket_min !== undefined || val.bucket_max !== undefined ){
            }else if( val.lte !== undefined || val.gte !== undefined ){
                invert = !invert
            }else if(option?.type === "segment_filter"){
            }else if(option?.type === "icon"){
                val = val.label
            }else{
                val = val.idx
            }
        }

        if( option.passType === "indexed"){
            return {type: "parent", value: val, pivot: option.access, relationship: option.relationship, invert}
        }

        if( option?.type === "category"){
            if( val === "_N_" || (val.length === 1 && val[0] === "_N_") ){
                return {type: "not_category_level1", value: option.primitiveId, pivot: option.access, invert, sourcePrimId: option.primitiveId, relationship: option.relationship}
            }
            if( val === "_N_"){
                val = undefined
            }else if(Array.isArray(val)){
                val = val.map(d=>d === "_N_" ? undefined : d)
            }
            return {type: "parent", value: val, pivot: option.access, relationship: option.relationship, invert, sourcePrimId: option.primitiveId}
        }else if( option?.type === "question" ){
            if( val === "_N_"){
                val = undefined
            }else if(Array.isArray(val)){
                val = val.map(d=>d === "_N_" ? undefined : d)
            }
            return {type: option.type, subtype: option.subtype, map: [val].flat(), pivot: option.access, relationship: option.relationship,  invert}
        }else if( option?.type === "type"){
            return {type: option.type, subtype: option.subtype, map: [val].flat().map(d=>parseInt(d)), pivot: option.access, relationship: option.relationship, invert}
        }else if( option?.type === "title" ){
            return  {type: "title", value: val, pivot: option.access, relationship: option.relationship, invert}
        }else if( option?.type === "icon"){
            return  {type: "parent", value: val, pivot: option.access, relationship: option.relationship, invert}
        }else if( option?.type === "segment_filter"){
            if( Array.isArray(val)){
                return  {type: "segment_filter", value: val,  pivot: option.access, relationship: option.relationship, invert}
            }
            return  {type: "parent", segmentFilter: true, value: val.idx ?? val, sourcePrimId: val.sourcePrimId, pivot: option.access, relationship: option.relationship, invert}
        }else if( option.type === "parameter"){
            if( val?.bucket_min  !== undefined ){
                return  {type: "parameter", param: option.parameter, value: {idx: val.idx, min_value: val.bucket_min, max_value: val.bucket_max}, pivot: option.access, relationship: option.relationship, invert}
            }else if( option.axisData?.buckets){
                const bucket = option.axisData?.buckets[val]
                return  {type: "parameter", param: option.parameter, value: {bucket: val, min_value: bucket.min, max_value: bucket.lessThan}, pivot: option.access, relationship: option.relationship, invert}
            }else{
                return  {type: "parameter", param: option.parameter, value: val, pivot: option.access, relationship: option.relationship, invert}
            }
        }else if( option.type === "structured_summary"){
            return  {type: "structured_summary", param: option.parameter, value: val, pivot: option.access, relationship: option.relationship, invert}
        } 
        return undefined
    },
    commonFilterSetup: (filter, isAxis = false )=>{
        //const needsValue = filter.type === "title" || filter.type === "parameter" || filter.type === "type" || filter.type === "parent"
        if( filter.segmentFilter){
            const {segmentFilter, axis, ...filterToRelay} = filter
            if( filterToRelay.type === "parameter" || filterToRelay.type === "title" || filterToRelay.type === "structured_summary"){
                delete filterToRelay["sourcePrimId"]
            }
            return {
                resolvedFilterType: "segment_filter",
                pivot: 1, 
                relationship: ["auto"], 
                check: filterToRelay,
                includeNulls: undefined,
                isRange: false
            }
        }
        const needsValue = filter.type === "type" || filter.type === "parent"
        let isRange = false
        if( !isAxis && needsValue && filter.value === undefined){
            return {skip:true}
        }
        if( filter.value ){
            if( Array.isArray(filter.value)){
                isRange = filter.value.find(d=>d?.gte !== undefined || d?.lte !== undefined || d?.min_value !== undefined || d?.max_value !== undefined )
            }else{
                isRange = filter.value.gte !== undefined || filter.value.lte !== undefined || filter.value.min_value !== undefined || filter.value.max_value !== undefined
            }
        }

        if( isRange ){
//            console.warn("RANGE NOT IMPLEMENTED")
        }
        let pivot = isAxis ? (filter.access ?? filter.pivot ?? filter.relationship?.length) : filter.pivot ?? filter.relationship?.length
        let relationship = filter.relationship
        let resolvedFilterType = filter.type
        
        if( filter.type === "category"){
            resolvedFilterType = "parent"
            if( Array.isArray(relationship)){
                relationship = [...relationship, "ref"]
            }else if(relationship){
                relationship = [relationship, "ref"]
            }else{
                relationship = ['ref']
            }
            pivot = (pivot ?? 0) + 1
        }
        if( filter.type === "segment_filter"){
            pivot = 1
            relationship = ['auto']
            if( Array.isArray(filter.value) ){
                resolvedFilterType = "parent"
            }
        }
        if( filter.type === "question"){
            pivot = (pivot ?? 0) + (filter.subtype === "question" ? 1 : 1)
            if( relationship){
                if( Array.isArray(relationship)){
                    relationship = [...relationship, "auto"]
                }else{
                    relationship = [relationship, "auto"]
                }
            }else{
                relationship = ["auto"]
            }
        }
        
        let check = [filter.map ?? filter.value].flat()
        let includeNulls = false

        if( filter.subtype === "question"){
            resolvedFilterType = "parent"
        }else if( filter.subtype === "search"){
            resolvedFilterType = "parent"
        }else if( filter.subtype === "prompt"){
            resolvedFilterType = "parent"
        }

        if( check.includes(undefined) || check.includes(null)){
            if( resolvedFilterType === "parent" || resolvedFilterType === "title"){
                check = check.filter(d=>d !== undefined && d !== null )
                includeNulls = true
            }else if(resolvedFilterType === "parameter" || resolvedFilterType === "structured_summary"){
                check = [undefined, ...check.filter(d=>d !== undefined && d !== null )]
            }
        }

        return {resolvedFilterType, pivot, relationship, check, includeNulls, isRange}

    },areArraysEqualIgnoreOrder:(arr1, arr2)=>{
        // Check if both arrays have the same length
        if( !arr1 || !arr2 ){
            return false
        }
        if (arr1.length !== arr2.length) {
            return false;
        }

        // Sort the arrays
        const sortedArr1 = arr1.slice().sort();
        const sortedArr2 = arr2.slice().sort();

        // Compare the sorted arrays element by element
        for (let i = 0; i < sortedArr1.length; i++) {
            if ((sortedArr1[i] === null || sortedArr1[i] === undefined) && (sortedArr2[i] === null || sortedArr2[i] === undefined)) {
                continue;
            }
            if (sortedArr1[i] !== sortedArr2[i]) {
                return false;
            }
        }

        // If all elements are equal, the arrays are equal
        return true;
    },
    processingErrors:(receiver, tasks)=>{
        const processing = receiver.processing ?? {}
        return Object.entries(processing).reduce((a,[k,v])=>{
            if( tasks && tasks.includes(k)){
                return a
            }
            if( v.error ){
                a[k] = v
            }
            return a
        }, {})

    },
    checkImports: (receiver, id, filters)=>{
        if( !filters || filters.length === 0){
            const imp = receiver.primitives?.imports
            if( imp ){

                const ids = imp.allIds ?? imp
                if( ids.includes(id) ){
                    if( !receiver?.referenceParameters?.target || receiver?.referenceParameters?.target === "items"){
                        if( !receiver.referenceParameters?.importConfig ){
                            return true
                        }
                        if( receiver.referenceParameters.importConfig.length === 1 ){
                            if(receiver.referenceParameters.importConfig[0].id === id){
                                if( !receiver.referenceParameters.importConfig[0].filters || receiver.referenceParameters.importConfig[0].filters.length === 0 ){
                                    return true
                                }
                            }
                        }
                        return false
                    }
                }
            }
            return false
        }
        if( (!receiver?.referenceParameters?.target || receiver?.referenceParameters?.target === "items") && receiver.referenceParameters?.importConfig){
            const candidates = receiver.referenceParameters.importConfig.filter(d=>d.id === id)
            const match = candidates.filter(d=> {
                if( !d.filters){
                    return false
                }
                const thisMatch = d.filters.filter(d2 => {
                    //console.log(`Checking`)
                    const thisSet = filters.find(ip=>{
                        const allKeys = [...Object.keys(d2), ...Object.keys(ip)].filter((d,i,a)=>d!== "id" && a.indexOf(d)===i)
                        return allKeys.reduce((a,c)=>{
                            let res = false
                            if( d2[c] instanceof Object){
                                if( Array.isArray(d2[c]) ){
                                    res = PrimitiveConfig.areArraysEqualIgnoreOrder( d2[c], ip[c])
                                }else if( d2[c] instanceof Object ){
                                    res = d2[c]?.idx === ip[c]?.idx
                                }else{
                                    throw `Param ${c} not processed`
                                }
                            }else{
                                res = (d2[c] === ip[c]) 
                            }
                            //console.log(" - ", res, d2[c], ip[c])
                            return res && a}, true)
                        })
                        //console.log(`Result = `, thisSet)
                        return thisSet
                })
                //console.log(thisMatch)
                return thisMatch.length === filters.length && thisMatch.length === d.filters.length
            })

            //console.log(match.length)
            if( match.length === 1){
                return true
            }
        }
        return false
    },getInputMap:(primitive, mode = "inputs")=>{
        const out = []
        const input_list = Object.keys(primitive.primitives?.[mode] ?? {})
        for(const inp of input_list){
            const [sourcePin, inputPin] = inp.split("_")
            const sourceIds = primitive.primitives[mode][inp]
            for(const sourceId of sourceIds){
                out.push( {sourceId, sourcePin, inputPin} )
            }
        }
        return out
        //return PrimitiveConfig.getPinMap(primitive, "inputs")
    },getOutputMap:(primitive)=>{
        const out = []
        const pp = primitive._parentPrimitives ?? primitive.parentPrimitives ?? {}
        const outputsToPins = Object.keys(pp).reduce((a,d)=>{
            const res = pp[d].filter(d=>d.startsWith("primitives.inputs."))
            if(res.length > 0){
                a[d] = res.map(d=>d.replace("primitives.inputs.",""))
            }
            return a
        },{})


        const outputToImports = Object.keys(primitive.primitives?.outputs ?? {}).reduce((a,d)=>{
            for( const target of primitive.primitives?.outputs[d]){
                a[target] ||= []
                a[target].push(d)
            }
            return a
        }, {})
        const evalToImports = Object.keys(primitive.primitives?.evals ?? {}).reduce((a,d)=>{
            for( const target of primitive.primitives?.evals[d]){
                a[target] ||= []
                a[target].push(d)
            }
            return a
        }, {})
        const list = {
            ...outputToImports,
            ...evalToImports,
            ...outputsToPins
        }


        for(const targetId of Object.keys(list)){
            for(const inp of list[targetId]){
                const [outputPin, targetPin] = inp.split("_")
                out.push( {targetId, targetPin, outputPin} )
            }
        }
        return out
        //return PrimitiveConfig.getPinMap(primitive, "outputs")
    },getPinsForAction:(targetCategory, name)=>{
        const activeAction = targetCategory?.actions?.find(d=>d.key === name)
        let actionPins = {}
        if( activeAction ){
            actionPins = Object.entries(activeAction.actionFields ?? {}).reduce((a,[k,d])=>{
                if( d.pin ){
                    a[k] = {
                        name: d.title,
                        types: ["string"]
                    }
                }
                return a
            },{})                    
        }
        return actionPins
    },getPinMap:(primitive, pins)=>{
        if( pins === "inputs"){
            return PrimitiveConfig.getInputMap(primitive)
        }else if( pins === "outputs"){
            return PrimitiveConfig.getOutputMap(primitive)
        }
    },getDynamicPins:(primitive, config, mode = "inputs")=>{
        if( mode === "inputs"){
            const collectPlaceholders = (sourceStrings = [])=>{
                const matches = sourceStrings
                    .filter(d => typeof d === "string" && d.length > 0)
                    .map(str=>str.match(/\{([^}]+)\}/g) ?? [])
                    .flat()
                const dynamicNames = matches.length > 0 ? matches.map(match => match.slice(1, -1)) : []
                // Count dot-notation references per root so pins can flag multi-dot usage.
                const dotRefCounts = {}
                for (const name of dynamicNames) {
                    if (name.includes(".")) {
                        const root = name.split(".")[0]
                        dotRefCounts[root] = (dotRefCounts[root] || 0) + 1
                    }
                }
                return dynamicNames.reduce((a,c)=>{
                    if( c.includes(".")){
                        const [root, ...remainder]  = c.split(".")
                        a[root] = {
                            types: ["primitive"],
                            decode: remainder.join("."),
                            multiDot: dotRefCounts[root] > 1
                        }
                    }else{
                        a[c] = {
                            types: ["string"]
                        }
                    }
                    return a
                }, {})
            }
            // A query whose prompt is a Viz DSL JSON declares its inputs via the
            // top-level `pins:` array, not via {placeholder} substitution. Detect
            // and extract from there. Accepts either a parsed object or a JSON
            // string. Falls through to placeholder collection for everything else.
            // Underscores collide with the pin-join delimiter, so DSL pin names
            // are normalized (_ → -) before being emitted. The crystallizer
            // applies the same normalization internally, so a DSL author can
            // write either form and the runtime stays consistent. Names starting
            // with "_" are internal sentinels and left untouched.
            const normalizePinName = (name) => {
                if (typeof name !== "string" || name.length === 0) return name
                if (name.startsWith("_")) return name
                return name.replace(/_/g, "-")
            }
            const collectDslPins = (raw) => {
                let dsl = raw
                if (typeof raw === "string") {
                    const trimmed = raw.trim()
                    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null
                    try { dsl = JSON.parse(trimmed) } catch { return null }
                }
                if (!dsl || typeof dsl !== "object" || !Array.isArray(dsl.pins)) return null
                const out = {}
                for (const pin of dsl.pins) {
                    if (!pin || typeof pin !== "object" || !pin.name) continue
                    // Always accept primitive or string. The crystallizer
                    // already JSON.parse-es string inputs in extractStructuredFromPin,
                    // so there's no functional benefit to constraining further
                    // based on the DSL's declared pin.type — it would only
                    // restrict what the UI lets you wire.
                    const normalizedName = normalizePinName(pin.name)
                    out[normalizedName] = {
                        name: pin.label || normalizedName,
                        types: ["primitive", "string"],
                        ...(pin.required ? { required: true } : {})
                    }
                }
                return out
            }
            if( primitive.type === "query" || primitive.type === "summary" || primitive.type === "categorizer"|| primitive.type === "action"){
                let src = config.prompt ?? config.query
                if( primitive.type === "categorizer" ){
                    src = [config.conditions ?? "", config.evaluation ?? "", config.cat_theme ?? ""].filter(d=>d).join(" ")
                }
                if( src ){
                    const dslPins = collectDslPins(src)
                    if (dslPins) return dslPins
                    return collectPlaceholders([src])
                }
            }else if( primitive.type === "search" ){
                const termSources = []
                if( Array.isArray(config?.terms)){
                    termSources.push(...config.terms)
                }else if( typeof config?.terms === "string"){
                    termSources.push(config.terms)
                }
                if( typeof config?.prefix === "string"){
                    termSources.push(config.prefix)
                }
                if( Array.isArray(config?.topic)){
                    termSources.push(...config.topic)
                }else if( typeof config?.topic === "string"){
                    termSources.push(config.topic)
                }
                if( termSources.length > 0 ){
                    return collectPlaceholders(termSources)
                }
            }else if( primitive.type === "actionrunner"){
                return config?.inputPins ?? {}
            }else if( primitive.type === "flowcontrol"){
                return {
                    ...(config.controlPins ?? {}),
                    ...(config.inputPins ?? {}),
                }
            }else if( primitive.type === "flow" ){
                return {
                    "title": {
                        "name": "Title",
                        "source": "title",
                        "types": ["string"]
                    },
                    "order": {
                        "name": "Order",
                        "types": ["string"]
                    },
                    "siblings": {
                        "name": "Siblings",
                        "types": ["string"]
                    },
                    ...(primitive.referenceParameters?.controlPins ?? {}),
                    ...(primitive.referenceParameters?.inputPins ?? {}),
                }
            }else if( primitive.type === "page"){
                return primitive.referenceParameters?.inputPins
            }
        }else{
            if( primitive.type === "flowcontrol"){
                return config.outputPins 
            }else if( primitive.type === "flow" ){
                return primitive.referenceParameters?.outputPins 
            }else if( primitive.type === "actionrunner" ){
                return config?.outputPins ?? {}
            }
        }
        return {}
    },canConnect:({input = {}, output = {}})=>{
        if( input.config && output.config){
            const toCheck = {
                inputMapConfig: input.config[input.pin],
                sourcePinConfig: output.config[output.pin]
            }
            console.log(toCheck)
            const result = PrimitiveConfig.alignInputAndSource([toCheck])
            return result[0]?.useConfig

        }
        return false

    },alignInputAndSource:(inputMap, dynamicPins)=>{
        let out = []
        for(const input of inputMap){
            let imConfig = input.inputMapConfig

            if(!imConfig && dynamicPins){
                input.inputMapConfig = dynamicPins[input.inputPin]
                imConfig = input.inputMapConfig
            }

            if( imConfig ){
                let sourcePinConfig = input.sourcePinConfig
                if( sourcePinConfig?.passThroughData){
                    out.push({
                        ...input,
                        useConfig: "pass_through"                        ,
                        sourceTransform: "source_data"
                    })
                }else if( imConfig?.types?.includes("anchored")){
                    input.sourcePinConfig = input.sourcePinConfig ?? {
                        name: "Anchored",
                        types: ["primitive"]
                    }
                    out.push({
                        ...input,
                        useConfig: "anchored",
                        sourceTransform: "anchored"
                    })
                }else if( input.sourcePin === "impout" || (input.sourcePin === "impin" && (input.sourcePrimitive?.type === "flow" || input.sourcePrimitive?.type === "flowinstance" || input.sourcePrimitive?.type === "flowcontrol"))){
                    if( input.inputMapConfig?.types?.includes("primitive")){
                        out.push({
                            ...input,
                            useConfig: "primitive",
                            sourcePinConfig: {
                                name:"Imports",
                                types:['primitive']
                            },
                            sourceTransform: ["result","evidence"].includes(input.sourcePrimitive.type) ? "pass_through" : "imports"
                        })
                    }else{
                        if( input.sourcePrimitive?.type === "flow"  || input.sourcePrimitive?.type === "flowinstance" ){
                            out.push({
                                ...input,
                                useConfig: "string",
                                sourcePinConfig: {
                                    name:"FlowInstance title",
                                    source: "title",
                                }
                            })
                        }else{
                            out.push({
                                ...input,
                                useConfig: "string",
                                sourcePinConfig: {
                                    name:"Imports",
                                    source: "BY_TYPE",
                                    types:['primitive']
                                },
                                sourceTransform: "imports"
                            })
                        }
                    }
                }else if( (input.sourcePrimitive?.type === "flow" || input.sourcePrimitive?.type === "flowinstance" || input.sourcePrimitive?.type === "flowcontrol") ){
                    if( input.sourcePin === "title"){
                         out.push({
                                ...input,
                                useConfig: "string",
                                sourcePinConfig: {
                                    name:"FlowInstance title",
                                    source: "title",
                                }
                            })
                    }else if( input.sourcePin === "order"){
                        out.push({
                            ...input,
                            useConfig: "string",
                            sourceTransform: "flow_order",
                            sourcePinConfig: {
                                name: "FlowInstance order",
                                types: ["string"]
                            }
                        })
                    }else{
                        if( sourcePinConfig?.types?.includes("primitive") || sourcePinConfig?.types?.includes("anchored")){
                            out.push({
                                ...input,
                                useConfig: "primitive",
                                sourceTransform: "pin_relay",
                            })
                        }else{
                            out.push({
                                ...input,
                                useConfig: "string",
                                sourceTransform: "pin_relay",
                            })
                        }
                    }
                }else{
                    if( !sourcePinConfig && (input.sourcePin === "rowAxis" || input.sourcePin === "colAxis")){
                        out.push({
                            ...input,
                            useConfig: "pass_through",
                            sourceTransform: "get_axis",
                            axis: input.sourcePin.slice(0,3)

                        })
                    }else  if( sourcePinConfig ){

                        let sourceTransform
                        let useConfig = sourcePinConfig.types.map(d=>({config:d, position: imConfig.types.indexOf(d)})).filter(d=>d.position > -1).reduce((best, current) => (best === null || current.position < best.position ? current : best), null)?.config
                        
                        if( !useConfig ){
                            if( sourcePinConfig.types.includes("object_list") ){
                                if( imConfig.types.includes("string_list") ){
                                    sourceTransform = "object_list_to_string"
                                    useConfig = "string_list"
                                }else if( imConfig.types.includes("string")){
                                    sourceTransform = "object_list_to_string"
                                    useConfig = "string"
                                }
                            }else if( sourcePinConfig.types.includes("string_list") && imConfig.types.includes("string")){
                                sourceTransform = "list_to_string"
                                useConfig = "string"
                            }else if( sourcePinConfig.types.includes("children") && imConfig.types.includes("string")){
                                sourceTransform = "child_list_to_string"
                                useConfig = "string"
                            }
                        }

                        out.push({
                            ...input,
                            useConfig,
                            sourceTransform
                        })

                    }                
                }
            }
        }
        return out

    },registerTransform:(name, config = {})=>{
        registerTransform(name, config);
    },getTransformDefinition:(name)=>{
        return getTransformDefinition(name);
    },getRegisteredTransforms:()=>{
        return Array.from(TRANSFORM_REGISTRY.keys());
    },hydrateInputEntriesAsync: async (entries = [], helpers = {})=>{
        if(!Array.isArray(entries)){
            return [];
        }
        for(const entry of entries){
            await runTransformAsync(entry, helpers);
        }
        return entries;
    },hydrateInputEntriesSync:(entries = [], helpers = {})=>{
        if(!Array.isArray(entries)){
            return [];
        }
        for(const entry of entries){
            runTransformSync(entry, helpers);
        }
        return entries;
    },
    // Extract text content from a single source primitive, mirroring the
    // resolution rules that translateInputMap uses internally. Centralized
    // here so other call sites (e.g. the dsl_transformer rebuild handler)
    // can reuse the same per-type extraction without duplicating the switch.
    //
    //   sourceField: a string from sourcePinConfig.source. Common values:
    //     - "title"     → return primitive.title.
    //     - "BY_TYPE"   → pick the natural text field for the primitive type:
    //         summary  → referenceParameters.summary
    //         action   → referenceParameters.result
    //         result   → description ?? context ?? title
    //         entity / other → title
    //     - "summary"   → with options.section, extract that section from
    //                     referenceParameters.structured_summary (handles the
    //                     list-style coercion when options.types includes
    //                     "string_list").
    //     - anything else → decodeParameter walk: referenceParameters first,
    //                     then the primitive itself, then options
    //                     .sourcePrimitiveConfig, then primitive.getConfig.
    //
    //   options:
    //     { section, types, sourcePrimitiveConfig, missingDecodeWarning }
    //
    extractPrimitiveSourceText: (primitive, sourceField, options = {}) => {
        if (!primitive) return ""
        if (sourceField === "title") return primitive.title

        let sf = sourceField

        // BY_TYPE: resolve to a per-type field. summary/action redirect into
        // the standard decodeParameter path below; result/other return early
        // because their text content is sometimes outside referenceParameters.
        if (sf === "BY_TYPE") {
            if (primitive.type === "summary") {
                sf = "summary"
            } else if (primitive.type === "action") {
                if( primitive.referenceParameters?.structured_summary){
                    const ss = primitive.referenceParameters?.structured_summary
                    if( Array.isArray(ss) ) {
                        if( ss.some(d=>d.heading)){
                            const flattend = flattenStructuredResponse(ss).trim()
                            if( flattend ){
                                return flattend
                            }
                        }else{
                            return ss
                        }
                    }
                }
                sf = "result"
            } else if (primitive.type === "result") {
                if (primitive.referenceParameters?.description) {
                    sf = "description"
                }
                return primitive.context ?? primitive.title
            } else {
                return primitive.title
            }
        }

        // Structured-summary section extraction.
        if (sf === "summary") {
            const section = options.section
            if (typeof section === "string" && section.trim().length > 0) {
                const structured = primitive.referenceParameters?.structured_summary
                if (structured) {
                    const subsection = findStructuredSummarySection(
                        structured,
                        s => s.heading === section
                    )
                    if (subsection) {
                        const wantList = Array.isArray(options.types) && options.types.includes("string_list")
                        if (wantList && subsection.content && subsection.type?.includes("list")) {
                            return subsection.content.match(/"[^"]*"|[^,\n]+/g)
                                .map(d => d.trim().replace(/^\s*-\s*/, ""))
                                .map(d => d.replace(/^"(.*)"$/, (_, capture) => capture))
                                .map(d => d.trim())
                                .join(",")
                        }
                        return flattenStructuredResponse(subsection)
                    }
                }
                return ""
            }
        }

        // Generic path lookup: referenceParameters first, then the primitive,
        // then a passed-in config fallback (mirrors translateInputMap's chain).
        let decoded = PrimitiveConfig.decodeParameter(primitive.referenceParameters, sf)
        if (decoded === undefined) {
            decoded = PrimitiveConfig.decodeParameter(primitive, sf)
        }
        if (!decoded) {
            if (options.sourcePrimitiveConfig) {
                decoded = PrimitiveConfig.decodeParameter(options.sourcePrimitiveConfig, sf)
            } else if (primitive.getConfig) {
                decoded = PrimitiveConfig.decodeParameter(primitive.getConfig, sf)
            }
        }
        return decoded
    },
    // Post-translate datasetView unwrap. The caller is expected to have
    // populated `entry._datasetViewConfig` on any interim entries whose
    // wired source (or its configParent) carries a `datasetView.enabled`
    // referenceParameters. Sync — both UI (in-memory configParent getter)
    // and server (async fetchPrimitive on configParent) feed the same shape
    // before invoking this, so the shared logic stays runtime-agnostic.
    //
    // For each pin where any contributing interim entry carries the config,
    // we replace the resolved primitive data with the configured fields read
    // off those primitives. The entry's `config` flips to "object" so
    // downstream code knows the items aren't full primitive docs.
    //
    // Single-source only — multi-source aggregation needs a port of
    // CollectionHelper.createDataTableFromDatasetView. A warning is logged
    // (via the passed `warn` callback) when the view wraps more than one
    // underlying data primitive so the call site is discoverable.
    unwrapDatasetViewEntries: (output, interim, warn) => {
        const log = typeof warn === "function" ? warn : (() => {})
        if (!output || typeof output !== "object" || !Array.isArray(interim)) return
        const dvByPin = new Map()
        for (const entry of interim) {
            if (!entry?._datasetViewConfig) continue
            if (!dvByPin.has(entry.inputPin)) dvByPin.set(entry.inputPin, [])
            dvByPin.get(entry.inputPin).push(entry)
        }
        for (const [pinName, entries] of dvByPin) {
            const out = output[pinName]
            if (!out || !Array.isArray(out.data) || out.config !== "primitive") continue
            if (entries.length > 1) {
                log(`pin "${pinName}" has ${entries.length} datasetView sources — using the first one. Multi-source aggregation isn't implemented.`)
            }
            const datasetView = entries[0]._datasetViewConfig
            const ownerLabel = `${entries[0].sourcePrimitive?.id ?? "?"}/${entries[0].sourcePrimitive?.plainId ?? "?"}`

            const fields = (datasetView.datasets || [])
                .map(d => typeof d === "string" ? d : d?.field)
                .filter(Boolean)
            if (datasetView.timelineField && !fields.includes(datasetView.timelineField)) {
                fields.push(datasetView.timelineField)
            }
            if (fields.length === 0) {
                log(`datasetView on ${ownerLabel} has no datasets configured — pin "${pinName}" returns empty data`)
                output[pinName] = { config: "object", data: [] }
                continue
            }

            const eligible = out.data.filter(p =>
                p?.referenceParameters && fields.some(f => p.referenceParameters[f] !== undefined)
            )
            if (eligible.length === 0) {
                log(`datasetView ${ownerLabel} has no resolved data primitives carrying any of: ${fields.join(", ")} — pin "${pinName}" returns empty data`)
                output[pinName] = { config: "object", data: [] }
                continue
            }
            if (eligible.length > 1) {
                log(`datasetView ${ownerLabel} resolves to ${eligible.length} data primitives — extracting from the first only.`)
            }
            const src = eligible[0]
            const extracted = {}
            for (const f of fields) {
                if (src.referenceParameters[f] !== undefined) extracted[f] = src.referenceParameters[f]
            }
            if (Object.keys(extracted).length > 0) {
                output[pinName] = { ...out, config: "object", data: [extracted] }
            }
        }
    },translateInputMap:(inputMap)=>{
        let out = {}
        function stringifyObjectListValue(value){
            if(value === null || value === undefined){
                return ""
            }
            if(Array.isArray(value)){
                return value.map(stringifyObjectListValue).filter(Boolean).join("; ")
            }
            if(typeof value === "object"){
                const preferredOrder = ["title","name","label","text","id"]
                const parts = []
                for(const key of preferredOrder){
                    if(typeof value[key] === "string" && value[key].trim()){
                        parts.push(value[key].trim())
                        break
                    }
                }
                if(typeof value.description === "string" && value.description.trim()){
                    parts.push(value.description.trim())
                }
                const extraKeys = Object.keys(value).filter(key=>!preferredOrder.includes(key) && key !== "description")
                for(const key of extraKeys){
                    const rendered = stringifyObjectListValue(value[key])
                    if(rendered){
                        parts.push(`${key}: ${rendered}`)
                    }
                }
                if(parts.length === 0){
                    return JSON.stringify(value)
                }
                return parts.join(" - ")
            }
            return `${value}`
        }
        for(const input of inputMap){
            let imConfig = input.inputMapConfig

            if( imConfig ){
                const source = input.sourcePrimitive 
                if( source ){
                    const sourcePinConfig = input.sourcePinConfig
                    if( sourcePinConfig || input.useConfig === "pass_through"){
                        if( !out[input.inputPin]){
                            out[input.inputPin] = {
                                config: input.useConfig,
                                data: []
                            }
                        }else{
                            if(out[input.inputPin].config !== input.useConfig ){
                                continue
                            }
                        }
                        if(input.useConfig === "primitive" || input.useConfig === "anchored"){
                            if( source.type === "page"){
                                    out[input.inputPin].data.push( source )
                            }else{
                                if( input.sources ){
                                    out[input.inputPin].data = out[input.inputPin].data.concat( input.sources )
                                }else{
                                    out[input.inputPin].data.push( source)
                                }
                            }
                            if( input.useConfig === "anchored" ){
                                out[input.inputPin].config = "primitive"
                            }
                            // When multiple dot notations reference the same root
                            // (e.g. {phase1.overview} + {phase1.executor}), skip
                            // inline decode so substitutePlaceholders gets the full
                            // primitive for resolveDotNotationValue.
                            const shouldSkipDecode = input.inputMapConfig?.multiDot === true
                            if( input.inputMapConfig.decode && !shouldSkipDecode){
                                out[input.inputPin].data = out[input.inputPin].data.map(d=>{
                                    if(d.type === "summary"){
                                        if( d.referenceParameters.structured_summary){
                                            const match = findStructuredSummarySection(
                                                d.referenceParameters.structured_summary,
                                                section => section.heading && section.heading.replaceAll(" ","_").toLowerCase() === input.inputMapConfig.decode
                                            )
                                            out[input.inputPin].config = "string"
                                            if( match ){
                                                return match.content
                                            }else{
                                                return ""
                                            }
                                        }
                                    }
                                    return d
                                })
                            }
                        }else if(input.useConfig === "pass_through"){
                            if( input.passThroughCoonfig ){
                                out[input.inputPin].config = input.passThroughCoonfig
                            }
                            out[input.inputPin].data.push( ...[input.pass_through].flat())
                        }else if(input.useConfig){
                            let sourceField = input.sourcePinConfig.source.replace(/^param./,"")
                            function extractDataFromSource( sources){
                                let result = []
                                // sourcePrimitiveConfig is only sensible as a
                                // fallback when there's a single source — for
                                // multi-source inputs we'd be using one config
                                // against many primitives. Old code logged in
                                // that case; we just suppress the fallback.
                                const passConfig = sources.length === 1 ? input.sourcePrimitiveConfig : undefined
                                let sourceData = sources.map(d=>{
                                    if(!d){return ""}
                                    return PrimitiveConfig.extractPrimitiveSourceText(d, sourceField, {
                                        section: input.inputMapConfig?.section,
                                        types: imConfig.types,
                                        sourcePrimitiveConfig: passConfig
                                    })
                                }).flat(Infinity)
                                const sourceTypes = input.sourcePinConfig?.types ?? []
                                if( sourceTypes.includes("object_list") && input.useConfig !== "object_list"){
                                    sourceData = sourceData.map(stringifyObjectListValue)
                                }
                                if( input.useConfig === "object_list"){
                                    result = result.concat( sourceData )
                                }else if(input.useConfig === "string_list"){
                                    let list
                                    if( sourceData.length  === 1){
                                        list = sourceData.flatMap(d=>{
                                            if( typeof(d)==="string"){
                                                if( d.includes("\n")){
                                                    return d.split("\n").map(d=>d.trim())
                                                }
                                                return d.split(",").map(d=>d.trim())
                                            }
                                            return d
                                        })
                                    }else{
                                        list = sourceData.flatMap(d=>typeof(d) === "string" ? d.trim() : d)
                                    }
                                    result = result.concat( list )
                                }else if(input.useConfig === "string"){
                                    if( Array.isArray(sourceData)){
                                        sourceData = sourceData.map(d=>{
                                            let raw
                                            if( typeof(d) === "string" ){
                                                raw = d
                                            }else if( d === undefined || d === null ){
                                                raw = ""
                                            }else{
                                                raw = JSON.stringify(d) ?? ""
                                            }
                                            let tx = raw.trim()

                                            if( sourceData.length > 1 && !tx.endsWith(".") && tx){
                                                tx = tx + ".  "
                                            }
                                            return tx
                                        }).join("")
                                    }
                                    result = sourceData
                                }
                                return result
                            }
                            let sources = input.sources ?? [source] 
                            out[input.inputPin].data = extractDataFromSource( sources )
                            out[input.inputPin].source = sources
                        }
                    }
                    

                }
            }
        }
        return out

    },
    convertEdgesToDepth( edges){
        const adj = new Map();        // from → [to,…]
        const indegree = new Map();   // node → # of incoming edges

        edges.forEach(({from, to}) => {
            if (!adj.has(from)) adj.set(from, []);
            if (!adj.has(to))   adj.set(to, []);
            adj.get(from).push(to);

            indegree.set(to, (indegree.get(to) || 0) + 1);
            if (!indegree.has(from)) indegree.set(from, 0);
        });
        const depth = new Map();  // node → its depth
        const q = [];

        for (let [node, d] of indegree.entries()) {
            if (d === 0) {
                q.push(node);
                depth.set(node, 0);
            }
        }
        while (q.length) {
            const u = q.shift();
            const d = depth.get(u);
          
            for (let v of adj.get(u)) {
              // assign depth[v] = max(existing, d+1)
              depth.set(v, Math.max(depth.get(v) || 0, d + 1));
          
              // “remove” edge u→v
              indegree.set(v, indegree.get(v) - 1);
              if (indegree.get(v) === 0) {
                q.push(v);
              }
            }
        }
        return depth
    },
    async evaluateFlowControl(step, {config = {}, statusMap = {}, functions = {}, options = {}} = {}){
        const states = Array.isArray(config.states) ? config.states : [];
        const result = {
            hasStates: states.length > 0,
            evaluated: false,
            activePins: {},
            matchedStates: [],
            haltFlow: false,
            pendingSources: [],
            stateResults: {}
        };
        if( states.length === 0 ){
            return result;
        }

        const connections = PrimitiveConfig.getInputMap(step);
        const inputToConnections = new Map();
        const primitiveCache = new Map();

        connections.forEach((entry)=>{
            if(!inputToConnections.has(entry.inputPin)){
                inputToConnections.set(entry.inputPin, []);
            }
            inputToConnections.get(entry.inputPin).push(entry);
        });

        async function fetchPrimitiveById(id){
            if(!id){
                return undefined;
            }
            if(primitiveCache.has(id)){
                return primitiveCache.get(id);
            }
            let fetched;
            if(typeof functions.fetchPrimitives === "function"){
                try{
                    fetched = await functions.fetchPrimitives([id]);
                }catch(err){
                    fetched = undefined;
                }
            }
            const prim = fetched?.[0];
            primitiveCache.set(id, prim);
            return prim;
        }

        const pendingSources = new Set();
        const countCache = new Map();

        function normalizeString(v){
            if( v === null || v === undefined ){
                return "";
            }
            return `${v}`.trim();
        }
        function compareValues(actual, operator, expected){
            const op = operator ?? "equals";
            if(["gt","gte","lt","lte"].includes(op)){
                const a = Number(actual ?? 0);
                const b = Number(expected ?? 0);
                if( op === "gt"){ return a > b; }
                if( op === "gte"){ return a >= b; }
                if( op === "lt"){ return a < b; }
                if( op === "lte"){ return a <= b; }
                return false;
            }
            if( op === "equals"){
                if( Array.isArray(expected) ){
                    return expected.some(v=>compareValues(actual, "equals", v));
                }
                return normalizeString(actual).toLowerCase() === normalizeString(expected).toLowerCase();
            }
            if( op === "not_equals"){
                return !compareValues(actual, "equals", expected);
            }
            if( op === "contains"){
                return normalizeString(actual).toLowerCase().includes(normalizeString(expected).toLowerCase());
            }
            if( op === "not_contains"){
                return !compareValues(actual, "contains", expected);
            }
            if( op === "starts_with"){
                return normalizeString(actual).toLowerCase().startsWith(normalizeString(expected).toLowerCase());
            }
            if( op === "ends_with"){
                return normalizeString(actual).toLowerCase().endsWith(normalizeString(expected).toLowerCase());
            }
            if( op === "in"){
                const list = Array.isArray(expected) ? expected : [expected];
                return list.some(v=>compareValues(actual, "equals", v));
            }
            if( op === "not_in"){
                return !compareValues(actual, "in", expected);
            }
            return false;
        }

        async function resolveCountForSource(sourceId, context = {}){
            if( countCache.has(sourceId) ){
                const cached = countCache.get(sourceId);
                if( context.inputPin && cached.byPin ){
                    return cached.byPin[context.inputPin] ?? cached.total;
                }
                return cached.total;
            }
            let total = 0;
            let byPin = {};
            if( typeof functions.countItemsForSource === "function"){
                try{
                    const res = await functions.countItemsForSource(step, sourceId, context);
                    if( typeof res === "number"){
                        total = res;
                    }else if( res && typeof res === "object"){
                        if( typeof res.total === "number"){
                            total = res.total;
                        }
                        if( res.byPin && typeof res.byPin === "object"){
                            byPin = res.byPin;
                        }
                    }
                }catch(err){
                    throw err
                    total = 0;
                }
            }else{
                const src = context.sourcePrimitive ?? await fetchPrimitiveById(sourceId);
                if( Array.isArray(src?.itemsForProcessing)){
                    total = src.itemsForProcessing.length;
                }
            }
            const value = {total, byPin};
            countCache.set(sourceId, value);
            if( context.inputPin && value.byPin){
                return value.byPin[context.inputPin] ?? value.total;
            }
            return value.total;
        }

        let hasPending = false;

        for(const state of states){
            const pinKey = state.pinKey || state.id;
            const conditions = Array.isArray(state.conditions) ? state.conditions : [];
            const aggregator = state.aggregator === "any" ? "any" : "all";
            result.activePins[pinKey] = false;
            const stateResult = {conditions: {}, pending: false};
            result.stateResults[state.id ?? pinKey] = stateResult;

            if( conditions.length === 0 ){
                stateResult.passed = true;
                result.activePins[pinKey] = true;
                result.matchedStates.push(state.id ?? pinKey);
                if( state.haltFlow ){
                    result.haltFlow = true;
                }
                continue;
            }

            let allTrue = true;
            let anyTrue = false;

            for(let idx = 0; idx < conditions.length; idx++){
                const condition = conditions[idx] ?? {};
                const condId = condition.id ?? `${pinKey}-cond-${idx}`;
                const inputPin = condition.inputPin;
                let passed = false;
                const candidates = (inputPin && inputToConnections.has(inputPin))
                    ? inputToConnections.get(inputPin)
                    : connections;

                let connection;
                for(const entry of candidates){
                    const sourcePrim = await fetchPrimitiveById(entry.sourceId);
                    if(condition.sourcePin && entry.sourcePin !== condition.sourcePin){
                        continue;
                    }
                    connection = {...entry, sourcePrimitive: sourcePrim};
                    break;
                }
                if( !connection && candidates.length > 0 ){
                    const entry = candidates[0];
                    connection = {...entry, sourcePrimitive: await fetchPrimitiveById(entry.sourceId)};
                }

                if( connection ){
                    const sourceId = connection.sourceId;
                    let sourceStatus = statusMap[sourceId];
                    if( !sourceStatus || sourceStatus.need ){
                        pendingSources.add(sourceStatus?.id ?? sourceId);
                        hasPending = true;
                        stateResult.pending = true;
                    }else{
                        let sourcePrimitive = await fetchPrimitiveById(sourceStatus.id ?? sourceId);
                        if( !sourcePrimitive ){
                            pendingSources.add(sourceStatus.id ?? sourceId);
                            hasPending = true;
                            stateResult.pending = true;
                        }else{
                            switch(condition.metric){
                                case "title":
                                    passed = compareValues(sourcePrimitive?.title ?? "", condition.comparator, condition.value);
                                    break;
                                case "parameter":{
                                    const path = condition.parameterPath;
                                    let paramValue;
                                    if( path ){
                                        paramValue = PrimitiveConfig.decodeParameter(sourcePrimitive?.referenceParameters ?? {}, path);
                                    }
                                    passed = compareValues(paramValue ?? "", condition.comparator, condition.value);
                                    break;
                                }
                                case "count":{
                                    const count = await resolveCountForSource(sourceId, {inputPin: connection?.inputPin, sourcePin: connection?.sourcePin, sourcePrimitive});
                                    passed = compareValues(count, condition.comparator ?? "gte", condition.value ?? 0);
                                    break;
                                }
                                case "error":{
                                    const runStatus = sourcePrimitive?.processing?.run_step?.status ?? sourceStatus?.needReason ?? "not_run";
                                    passed = compareValues(runStatus, condition.comparator ?? "equals", condition.value ?? "error");
                                    break;
                                }
                                default:
                                    passed = false;
                            }
                        }
                    }
                }else{
                    passed = false;
                }
                anyTrue = anyTrue || passed;
                allTrue = allTrue && passed;
                stateResult.conditions[condId] = passed;
            }

            if( stateResult.pending ){
                continue;
            }

            const statePassed = aggregator === "any" ? anyTrue : allTrue;
            stateResult.passed = statePassed;

            if( statePassed ){
                result.activePins[pinKey] = true;
                result.matchedStates.push(state.id ?? pinKey);
                if( state.haltFlow ){
                    result.haltFlow = true;
                }
            }
        }

        result.pendingSources = Array.from(pendingSources);
        result.evaluated = !hasPending && result.pendingSources.length === 0;
        return result;
    },
    flowInstanceStatusToMap( statusMap, {showHidden, showSkipped, groupByLabels}){
        const visibleIds = new Set(
            Object.entries(statusMap).filter(([id, info]) => {
                const refParams = (info.primitive.configParent ?? info.primitive).referenceParameters;
                const isVisible = showHidden ? true  : refParams?.showInMap !== false;
                const isNotSkipped = showSkipped ? true : !info.skip;
                return isVisible && isNotSkipped;
              })
              .map(([id]) => id)
          );
    
          // 2) Build a lookup from each node ID → its direct children IDs
          const childrenMap = {};
          Object.values(statusMap).forEach(({ id, children }) => {
            childrenMap[id] = (children || []).map((c) => c.id);
          });
    
          // 3) Helper: collect all visible descendants of a node, skipping over hidden ones
          const collectVisibleDescendants = (nodeId, visited = new Set()) => {
            const targets = new Set();
            const stack = [...(childrenMap[nodeId] || [])];
            while (stack.length) {
              const curr = stack.pop();
              if (visited.has(curr)) continue;
              visited.add(curr);
    
              if (visibleIds.has(curr)) {
                targets.add(curr);
              } else {
                // dive into this hidden node’s children
                (childrenMap[curr] || []).forEach((gc) => {
                  if (!visited.has(gc)) stack.push(gc);
                });
              }
            }
            return targets;
          };
    
          // 4) If groupByLabels: build label→IDs map for visible nodes
          let labelGroups = null;
          if (groupByLabels) {
            labelGroups = {};
            visibleIds.forEach((id) => {
              // assume “label” is at info.primitive.configParent.label or info.primitive.label
              const {children, ...info} = statusMap[id];
              const label = (info.primitive.configParent ?? info.primitive).referenceParameters.labelForMap;
              const icon = info.primitive.configParent?.metadata?.icon
              if (label) {
                if (!labelGroups[label]) labelGroups[label] = {ids:[], items:[], icon};
                labelGroups[label].ids.push(id);
                labelGroups[label].items.push(info);
              }
            });
          }
    
          // 5) Determine representative ID for each visible node (possibly remapped by label)
          //    repMap[id] = representative ID (either its label or itself)
          const repMap = {};
          if (groupByLabels) {
            // for each label group, pick the label string as the rep ID
            Object.entries(labelGroups).forEach(([label, {ids}]) => {
              ids.forEach((id) => {
                repMap[id] = `label:${label}`;
              });
            });
          }
          // for any visible ID not in repMap, it stays as itself
          visibleIds.forEach((id) => {
            if (!repMap[id]) repMap[id] = id;
          });
    
          // 6) Build nodeList: one entry per unique repMap value
          const seenReps = new Set();
          const nodeList = [];
          visibleIds.forEach((id) => {
            const rep = repMap[id];
            if (seenReps.has(rep)) return;
            seenReps.add(rep);
    
            if (rep.startsWith("label:")) {
              // grouped node: label = rep.slice(6)
              let label = rep.slice(6);
              const labelGroup = labelGroups[label]
              if( labelGroup.ids.length > 1){
                label += ` (${labelGroup.ids.length})`
              }
              nodeList.push({
                id: rep,
                name: label,
                _itemIds: labelGroup.items.map(d=>d.primitive.id),
                itemIds: labelGroup.ids,
                status: ()=>{
                    const itemStatus = labelGroup.items.map(d=>d.primitive.processing?.run_step?.status === "pending" ? "waiting" : (d.primitive.processing?.flow?.status ?? "not_run")).filter((d,i,a)=>a.indexOf(d)===i)
                    let groupStatus = "not_run"
                    if( itemStatus.includes("error")){
                        groupStatus = "error"
                    }else if( itemStatus.includes("error_skip")){
                        groupStatus = "error_skip"
                    }else if( itemStatus.includes("rerun")){
                        groupStatus = "rerun"
                    }else if( itemStatus.includes("running")){
                        groupStatus = "running"
                    }else if( itemStatus.includes("waiting") || itemStatus.includes("pending")){
                        groupStatus = "waiting"
                    }else if( itemStatus.includes("not_complete")){
                        groupStatus = "not_complete"
                    }else if( itemStatus.includes("complete")){
                        groupStatus = "complete"
                    }
                    return groupStatus
                },
                progress:()=>{
                  const progressList = labelGroup.items.map(d=>d.primitive.percentageProgress)
                  const progress = Math.min(...progressList.filter(d=>d!==undefined))
                  return progress
                },
                progressMessage: ()=>{
                    const metrics = labelGroup.items.reduce((acc, entry)=>{
                        const stats = entry?.primitive?.progressStats
                        if( !stats ){
                            return acc
                        }
                        const totalCount = Number(stats.totalCount)
                        const totalScanned = Number(stats.totalScanned ?? stats.scanned)
                        const max = Number(stats.max)
                        if( Number.isFinite(totalCount) ){
                            acc.totalCount += totalCount
                            acc.hasNumeric = true
                        }
                        if( Number.isFinite(totalScanned) ){
                            acc.totalScanned += totalScanned
                            acc.hasNumeric = true
                        }
                        if( Number.isFinite(max) ){
                            acc.max += max
                            acc.hasMax = true
                        }
                        return acc
                    }, {totalCount: 0, totalScanned: 0, max: 0, hasNumeric: false, hasMax: false})

                    if( metrics.hasNumeric ){
                        const maxPart = metrics.hasMax ? ` / max ${metrics.max}` : ""
                        return `Found ${metrics.totalCount} items (scanned ${metrics.totalScanned}${maxPart})`
                    }

                    const progressList = labelGroup.items.map(d=>d.primitive.progress).filter(Boolean)
                    const progressCount = progressList.length
                    if( progressCount === 0){
                        return
                    }
                    if( progressCount === 1){
                        return progressList[0]
                    }
                    return progressList.slice(0,3).join("\n") + (progressCount > 3 ? `\n+${progressCount - 3} others` : "")
                },
                skipped: false,
                candidateForRun: labelGroup.items.some(d=>d.candidateForRun),
                icon: labelGroup.icon
              });
            } else {
              // ungrouped, use original info
              const info = statusMap[id];
              nodeList.push({
                id,
                itemIds: [info.primitive.id],
                name: (info.primitive.configParent ?? info.primitive).title,
                status: ()=>{
                    let status = info.primitive.processing?.run_step?.status === "pending" ? "waiting" : (info.primitive.processing?.flow?.status ?? "not_run")
                    if( status === "pending"){
                        return "waiting"
                    }                   
                    return status     
                },
                progress: ()=>info.primitive.percentageProgress,
                progressMessage: ()=>info.primitive.progress,
                //progress: ()=>({percentage: 0.35}),
                candidateForRun: info.candidateForRun,
                skipped: info.skip,
                icon: info.primitive.configParent?.metadata?.icon,
                primitiveType: info.primitive.type,
                flowControl: info.flowControl,
                flowControlStates: info.primitive.referenceParameters?.flowControl?.states ?? [],
              });
            }
          });
    
          // 7) Build edgeList: for each visible “parent” ID, collect its visible descendants,
          //    then map parent→rep and child→rep, skipping self‐loops and duplicates.
          const edgeSet = new Set();
          visibleIds.forEach((parentId) => {
            const descendants = collectVisibleDescendants(parentId);
            descendants.forEach((childId) => {
              const repParent = repMap[parentId];
              const repChild = repMap[childId];
              if (repParent !== repChild) {
                const key = `${repParent}->${repChild}`;
                if (!edgeSet.has(key)) {
                  edgeSet.add(key);
                }
              }
            });
          });
    
          const edgeList = Array.from(edgeSet).map((key) => {
            const [from, to] = key.split("->");
            return { from, to };
          });
    
          return { nodes: nodeList, edges: edgeList, visibleIds: [...visibleIds] };
    },
    async buildFlowInstanceStatus(flowInstance, steps, functions = {}, options = {}){
        function getOrigin( item ){
            return Object.entries(item._parentPrimitives ?? item.parentPrimitives ?? {}).find(d=>d[1].includes("primitives.origin"))?.[0]
        }
        function getConfigId( item ){
            return Object.entries(item._parentPrimitives ?? item.parentPrimitives ?? {}).find(d=>d[1].includes("primitives.config"))?.[0]
        }

        steps = steps.slice().filter(d=>d)
        const stepIdsSet = new Set( steps.map(d=>d.id) )
        const flowStepIdsSets = new Set( steps.map(d=>getConfigId(d)) )
        let flowStarted = flowInstance.processing?.flow?.started
        const map = {}


        //const flowId = Object.entries(flowInstance._parentPrimitives ?? flowInstance.parentPrimitives ?? {}).find(d=>d[1].includes("primitives.origin"))?.[0]
        const flowId = getOrigin(flowInstance)
        
        const normalizeConfigValues = (value) =>
            [value]
                .flat()
                .map((entry)=>`${entry ?? ""}`.trim())
                .filter((entry)=>entry.length > 0);

        const fcValues = Object.entries(flowInstance.referenceParameters ?? {}).reduce((a,[k,v])=>{
            if( k.startsWith("fc_")){
                const normalizedValues = normalizeConfigValues(v)
                if( normalizedValues.length > 0 ){
                    a[k] = normalizedValues
                }
            }
            return a
        }, {})


        function isIsoDate(value){
            return typeof value === "string" && !Number.isNaN(Date.parse(value));
        }

        function toIsoDate(value){
            if (!value) {
                return null;
            }
            const isoString = value instanceof Date ? value.toISOString() : value;
            return isIsoDate(isoString) ? isoString : null;
        }

        const flowStartedIso = toIsoDate(flowStarted) ?? flowStarted;

        function isDependencyStatusReady(status){
            if (!status) {
                return false;
            }
            if (status.skip || status.skipDownstream) {
                return true;
            }
            if (status.need) {
                return false;
            }
            const reason = status.needReason;
            if (!reason) {
                return false;
            }
            if (reason === "complete" || reason === "ignored" || reason === "error_ignore") {
                return true;
            }
            return false;
        }

        async function setupStep( step, child ){
            const stepConfigId = getConfigId(step)
            if( map[step.id]){
                if( child ){
                    if(!map[step.id].children.find(d=>d.id === child.id)){
                        map[step.id].children.push( child )
                    }
                }
                return
            }
            
            const pp = functions.getPrimitives ? functions.getPrimitives(step) : step.primitives
            const checkListIds = [
                ...pp.imports.allIds, 
                ...pp.inputs.allIds,
            ]
            if( step.type === "view" || step.type === "query" ){
                checkListIds.push(...pp.axis.column.allIds, ...pp.axis.row.allIds)
            }
            const checkList = checkListIds.map(d=>{
                const match = steps.find(d2=>d2.id === d)
                if( match ){
                    return match
                }
            }).filter(d=>d)

            let config = {}
            if( options.configPrimitives){
                if( stepConfigId ){
                    const configPrimitve = options.configPrimitives.find(d=>d.id === stepConfigId)
                    config = configPrimitve?.referenceParameters ?? {}
                }else{
                    console.log(`>> Couldnt get config Id`)
                }
            }else{
                config = functions.getConfig ? (await functions.getConfig(step)) : step.getConfig
            }
            const stepControlValues = Object.entries(config ?? {}).reduce((a,[k,v])=>{
                if( k.startsWith("fc_")){
                    const normalizedValues = normalizeConfigValues(v)
                    if( normalizedValues.length > 0 ){
                        a.push([k, normalizedValues])
                    }
                }
                return a
            }, [])
            let matchingConfig  = stepControlValues.some(([k, allowedValues])=>{
                const selectedValues = fcValues[k]
                return selectedValues && selectedValues.some((selected)=>allowedValues.includes(selected))
            })
            const skipForConfiguration = stepControlValues.length > 0 && !matchingConfig

            const status = {
                id: step.id,
                routing: step.type === "segment",
                skipForConfiguration,
                skipForUpstream: undefined,
                need: false,
                can: undefined,
                children: child ? [child] : []
            }
            if( options.withPrimitives){
                status.primitive = step
            }
            let skipForImport = 0
            const importIds = pp.imports.allIds
            for(const p of checkList){
                await setupStep( p, status )
                if( map[p.id]){
                    let thisSkipped = (map[p.id].skip === true || map[p.id].skipDownstream === true) ? true : false
                    if( importIds.includes(p.id)){
                        if( thisSkipped ){
                            skipForImport++
                        }
                    }else{
                        if( !thisSkipped && skipForImport > 0 && skipForImport === importIds.length){
                            console.log(`Step ${step.id} has all imports skipped - ignoring input status ${thisSkipped} of ${p.id} / ${p.plainId}`)
                            thisSkipped = true
                        }
                    }
                    if( status.skipForUpstream === undefined ){
                        status.skipForUpstream = thisSkipped
                    }else{
                        status.skipForUpstream = thisSkipped && status.skipForUpstream
                    }
                }
            }

            status.skip = status.skipForConfiguration || status.skipForUpstream

            function applyFlowControlGating(){
                const inputConnections = PrimitiveConfig.getInputMap(step);
                const importConnections = importIds.map(d=>{
                    const parentStatus = map[d];
                    if( parentStatus){
                        const [connectPin,_] = Object.entries( parentStatus.primitive?.primitives?.outputs ?? {}).find(([k, v])=>v.includes(step.id)) ?? []
                        if( connectPin ){
                            const [outPin, inPin] = connectPin.split("_")
                            return {sourceId: d, sourcePin: outPin}
                        }
                    }
                }).filter(Boolean)
                const allConnections = [...inputConnections, ...importConnections];
                const gatingEntries = allConnections.reduce((acc, entry)=>{
                    const parentStatus = map[entry.sourceId];
                    if( parentStatus?.primitive?.type !== "flowcontrol"){
                        return acc;
                    }
                    if( !acc.has(entry.sourceId)){
                        acc.set(entry.sourceId, []);
                    }
                    acc.get(entry.sourceId).push(entry);
                    return acc;
                }, new Map());
                if( gatingEntries.size === 0 ){
                    return;
                }
                for(const [flowControlId, entries] of gatingEntries.entries()){
                    const parentStatus = map[flowControlId];
                    const fc = parentStatus?.flowControl;
                    if( !fc || !fc.evaluated ){
                        status.need = true;
                        status.needReason = "flow_control_pending";
                        status.can = false;
                        status.canReason = "flow_control_pending";
                        status.candidateForRun = false;
                        return;
                    }
                    const anyActive = entries.some(entry=>fc.activePins?.[entry.sourcePin] === true);
                    if( !anyActive ){
                        status.need = false;
                        status.needReason = "flow_control_inactive";
                        status.can = false;
                        status.canReason = "flow_control_inactive";
                        status.skip = true;
                        status.skipDownstream = true;
                        status.candidateForRun = false;
                        return;
                    }
                }
            }
            async function applyConfirmationGating(){
                const confirmationRequired = config?.fcWaitForConfirmation === true;
                const autoRunRaw = flowInstance?.referenceParameters?.auto_run;
                const autoRunEnabled =
                    autoRunRaw === true ||
                    autoRunRaw === 1 ||
                    (typeof autoRunRaw === "string" && ["true", "1", "yes", "on"].includes(autoRunRaw.trim().toLowerCase()));
                const existingConfirmation = step.processing?.flow?.confirmation ?? {};
                const confirmedForFlowStarted = existingConfirmation?.confirmedForFlowStarted;
                const confirmedAt = existingConfirmation?.confirmedAt;
                const confirmedBy = existingConfirmation?.confirmedBy;
                const confirmedForCurrentRun = confirmationRequired && flowStarted && confirmedForFlowStarted === flowStarted;

                status.confirmation = {
                    required: confirmationRequired,
                    confirmedForFlowStarted,
                    confirmedAt,
                    confirmedBy,
                    confirmedForCurrentRun: Boolean(confirmedForCurrentRun),
                    autoRunBypass: autoRunEnabled,
                };

                if (typeof functions.dispatchControlUpdate === "function") {
                    const normalizedExisting = {
                        required: existingConfirmation?.required === true,
                        confirmedForFlowStarted: existingConfirmation?.confirmedForFlowStarted ?? null,
                        confirmedAt: existingConfirmation?.confirmedAt ?? null,
                        confirmedBy: existingConfirmation?.confirmedBy ?? null,
                    };
                    const desiredConfirmation = confirmationRequired
                        ? {
                              required: true,
                              confirmedForFlowStarted: confirmedForFlowStarted ?? null,
                              confirmedAt: confirmedAt ?? null,
                              confirmedBy: confirmedBy ?? null,
                          }
                        : {
                              required: false,
                              confirmedForFlowStarted: null,
                              confirmedAt: null,
                              confirmedBy: null,
                          };
                    const hasConfirmationChange = ["required", "confirmedForFlowStarted", "confirmedAt", "confirmedBy"].some(
                        (key) => normalizedExisting[key] !== desiredConfirmation[key]
                    );
                    if (hasConfirmationChange) {
                        await functions.dispatchControlUpdate(step.id, "processing.flow.confirmation", desiredConfirmation);
                    }
                }

                const stepCompletedForCurrentRun = status.needReason === "complete" && status.need === false;
                if (confirmationRequired && stepCompletedForCurrentRun && !status.running && !confirmedForCurrentRun && !autoRunEnabled) {
                    status.need = true;
                    status.needReason = "awaiting_confirmation";
                    status.can = false;
                    status.canReason = "awaiting_confirmation";
                    status.candidateForRun = false;
                    status.pausedForConfirmation = true;
                }
            }

            const streamedExternal = step.type === "external" && isStreamingConfigEnabled(config);
            if( !status.skip && !status.routing){
                if( 
                    (step.type === "flowinstance" && flowStarted && new Date(step.processing?.flow?.started) >= new Date(flowStarted)) ||
                    (step.type !== "flowinstance" && flowStarted && step.processing?.flow?.started === flowStarted)
                ){
                    if(step.processing?.flow?.status === "error"){
                        status.needReason = step.processing?.flow?.status
                        status.error = step.processing?.flow?.error ?? "Error"
                    }else if(step.processing?.flow?.status === "error_skip"){
                        status.needReason = step.processing?.flow?.status
                        status.error = step.processing?.flow?.error ?? "Error"
                        status.skipDownstream = true
                    }else if(step.processing?.flow?.status === "error_ignore" || step.processing?.flow?.status === "complete"){
                        status.needReason = "complete"
                    }else{
                        status.need = true
                        status.needReason = "not_complete"
                    }
                }else{
                    status.need = true
                    status.needReason = "not_executed"
                }
                if( step.processing?.flow?.status === "ignore"){
                    status.need = false
                    status.needReason = "ignored"
                }
                if( status.need ){
                    if( functions.isStepRunning ){
                        status.running = await functions.isStepRunning( step )
                    }
                }
                if( status.need ){
                    if( flowStepIdsPendingSubFlowInstance.has( stepConfigId )){
                       status.can = false 
                       status.canReason = "subflow_pending" 
                    }else{

                        const {can, canReason} = await PrimitiveConfig.canStepRun( flowInstance, step, map, functions, options)
                        status.can = can
                        status.canReason = canReason
                    }
                    
                }
                applyFlowControlGating();
                await applyConfirmationGating();
                if( streamedExternal && status.need && status.needReason !== "complete" && !status.running ){
                    const hasTriggerRequest = step.processing?.stream?.triggerRequest?.requested === true;
                    if( !hasTriggerRequest ){
                        status.need = false;
                        status.needReason = AWAITING_UPSTREAM_REASON;
                        status.can = false;
                        status.canReason = "stream_waiting";
                    }
                }
            }
            if( step.type === "flowcontrol"){
                status.flowControl = await PrimitiveConfig.evaluateFlowControl(step, {
                    config: config?.flowControl ?? {},
                    statusMap: map,
                    functions,
                    options
                });
                status.need = false;
                status.needReason = status.flowControl?.evaluated ? "flow_control" : "flow_control_pending";
                status.can = false;
                status.canReason = status.flowControl?.evaluated ? "flow_control" : "flow_control_pending";
                if( options.withPrimitives && status.primitive && status.flowControl ){
                    const targetPrimitive = status.primitive;
                    const flowStartedAt = flowInstance?.processing?.flow?.started ?? flowStarted ?? null;
                    const flowControlSnapshot = {
                        ...status.flowControl,
                        flowRunId: flowStartedAt,
                        evaluatedAt: new Date().toISOString()
                    };
                    if( typeof functions.dispatchControlUpdate === "function" ){
                        try{
                            const primitiveId = targetPrimitive?.id ?? targetPrimitive?._id?.toString?.();
                            if( primitiveId ){
                                await functions.dispatchControlUpdate(primitiveId, "processing.flowControl", flowControlSnapshot);
                            }
                        }catch(err){
                            console.log(`Failed to dispatch flow control update for ${targetPrimitive?.id ?? targetPrimitive}`);
                            console.log(err);
                        }
                    }
                }
            }
            status.candidateForRun = status.can && status.need
            map[step.id] = status
        }

        const flowStepIdsPendingSubFlowInstance = new Set()

        if( options.subFlowsToScaffold ){
            for(const subFlow of options.subFlowsToScaffold ){
                const need = flowInstance.processing?.flow?.subFlow?.[subFlow.id]?.checked !== flowStarted
                const status = {
                    id: subFlow.id,
                    need: false,
                    needReason: need ? AWAITING_UPSTREAM_REASON : undefined,
                    can: undefined,
                    canReason: need ? AWAITING_UPSTREAM_REASON : undefined,
                    children: [],
                    dependencies: [],
                    pendingDependencies: [],
                    isSubFlowScaffold: true
                }
                if( options.withPrimitives){
                    status.primitive = subFlow
                }
                map[status.id] = status
                    
                const pp = functions.getPrimitives ? functions.getPrimitives(subFlow) : subFlow.primitives
                const checkListIds = [
                    ...pp.imports.allIds, 
                    ...pp.inputs.allIds,
                ]
                console.log(`Missing subflow origin is ${checkListIds.join(", ")}`)
                
                const relevantSubFlowInstances = pp.config.allIds.filter(d=>stepIdsSet.has(d))
                const dependentFlowStepIds = Object.entries(subFlow._parentPrimitives ?? subFlow.parentPrimitives ?? {}).filter(([k, v])=>v.includes("primitives.input") || v.includes("primitives.imports")).map(d=>d[0]).filter(d=>flowStepIdsSets.has(d))

                map[status.id].subFlowInstancesPresent = relevantSubFlowInstances.length
                map[status.id].dependentFlowStepIds = dependentFlowStepIds
                
                if( map[status.id].subFlowInstancesPresent === 0){
                    flowStepIdsPendingSubFlowInstance.add( ...map[status.id].dependentFlowStepIds )
                }


                let dependenciesReady = true
                for(const id of checkListIds ){
                    const resolved = steps.find(d=>getConfigId(d) === id)
                    console.log(`Resolved ${id} to ${resolved?.id} / ${resolved?.plainId} / ${resolved?.title}`)
                    let dependencyReady = !need
                    if( resolved ){
                        await setupStep( resolved, status)
                        status.dependencies.push(resolved.id)
                        const dependencyStatus = map[resolved.id]
                        if( dependencyStatus?.skip ){
                            continue
                        }
                        if( dependencyStatus?.routing ){
                            continue
                        }
                        if( need ){
                            const hasValidStart    = flowInstance.processing?.flow?.started !== undefined;
                            const otherFlowStarted = toIsoDate(resolved.processing?.flow?.started);
                            const isComplete       = resolved.processing?.flow?.status === "complete" || resolved.processing?.flow?.status === "error_ignore";
                            const isIgnored        = resolved.processing?.flow?.status === "ignore";
                            const timingOk = hasValidStart
                                ? (!!otherFlowStarted && (!!flowStartedIso ? otherFlowStarted <= flowStartedIso : true))
                                : !!otherFlowStarted;

                            dependencyReady = isIgnored || isDependencyStatusReady(dependencyStatus) || (isComplete && timingOk);
                        }
                    }else{
                        if( getOrigin(flowInstance) === id){
                            dependencyReady = true
                        }else{
                            console.log(`Couldnt resolve ${id} in flowinstance status check`)
                        }
                    }
                    if( need && !dependencyReady ){
                        status.pendingDependencies.push(id)
                    }
                    if( need ){
                        dependenciesReady &&= dependencyReady
                    }
                }
                if( need && dependenciesReady ){
                    status.need = true
                    status.needReason = "scaffold_flow"
                    status.can = true
                    status.canReason = "all_ready"
                }else if( need ){
                    status.need = false
                    status.needReason = AWAITING_UPSTREAM_REASON
                    status.can = false
                    status.canReason = AWAITING_UPSTREAM_REASON
                }else{
                    status.need = false
                    status.needReason = status.needReason ?? undefined
                    status.can = dependenciesReady
                    status.canReason = dependenciesReady ? "all_ready" : AWAITING_UPSTREAM_REASON
                }
            }
        }

        
        for( const step of steps){
            await setupStep(step)
        }
        for(const id of Object.keys(map)){
            const thisItem = map[id]
            if( thisItem.routing ){
                const nodesToUpdate = Object.values(map).filter(d=>d.children.some(d2=>d2.id === thisItem.id))
                for(const updateNode of nodesToUpdate){
                    updateNode.children = updateNode.children.filter(d=>d.id !== thisItem.id)
                    updateNode.children.push(...thisItem.children)
                }
                delete map[id]
            }
        }
        return map

    },async canStepRun(flowInstance, step, stepState, functions = {}, options = {}){
        let flowStarted = flowInstance.processing?.flow?.started
        let can = true
        let canReason = "all_ready"
        const autoRunRaw = flowInstance?.referenceParameters?.auto_run;
        const flowInstanceAutoRunEnabled =
            autoRunRaw === true ||
            autoRunRaw === 1 ||
            (typeof autoRunRaw === "string" && ["true", "1", "yes", "on"].includes(autoRunRaw.trim().toLowerCase()));

        async function fetchImports( importIds ){
            if( !options.cache ){
                return await functions.fetchPrimitives(importIds)
            }
            let importPrimitives = importIds.map(d=>options.cache[d]).filter(d=>d)
            if( importPrimitives.length !== importIds.length){
                console.log(`-- Cache miss - fetching importIds`)
                importPrimitives = await functions.fetchPrimitives( importIds )
            }
            return importPrimitives
        } 


        async function checkOutstandingSource( rel ){
            let can = true
            let inAncestor = false
            const pp = functions.getPrimitives(step).fromPath(rel)
            const importIds = pp.uniqueAllIds
            if( importIds.length > 0){
                const importPrimitives = await fetchImports( importIds)
                for(const baseImp of importPrimitives){
                    if( !can ){
                        continue
                    }
                    const baseId = baseImp.id
                    if( !stepState[baseId] ){
                        if( baseId !== flowInstance.id){
                            console.warn( `No state for ${baseId} when processing ${step.id}`)
                        }
                    }else{
                        const upstreamConfirmation = stepState[baseId].confirmation;
                        const confirmationBypassed = upstreamConfirmation?.autoRunBypass === true || flowInstanceAutoRunEnabled;
                        if( upstreamConfirmation?.required && upstreamConfirmation?.confirmedForCurrentRun !== true && !confirmationBypassed ){
                            can = false
                            continue
                        }
                        if( stepState[baseId].skip ){
                            continue
                        }
                        if( stepState[baseId].routing ){
                            continue
                        }
                        if( stepState[baseId].primitive?.type === "flow" ){
                            if( stepState[baseId].needReason === "scaffold_flow" || stepState[baseId].needReason === AWAITING_UPSTREAM_REASON){
                                can = false
                                console.log(`Cant run for flow ${baseId} - waiting for scaffold`)
                            }else{
                                console.log(`Ignoring state of ${baseId} already scaffold so rely on flowinstance state`)
                            }
                            continue
                        }
                        if( stepState[baseId].primitive?.type === "flowcontrol" ){
                            continue
                        }
                    }
                    let imp = baseImp
                    const originId = Object.entries(imp._parentPrimitives ?? imp.parentPrimitives ?? {}).find(d=>d[1].includes("primitives.origin"))?.[0]
                    if( imp.type === "segment"){
                        if( step.type === "flowinstance"){
                            console.log(`Got segment import for instance of sub flow`)
                            const parentStep = (await fetchImports( [originId] ))[0]
                            if( !parentStep || (parentStep.id !== flowInstance.id && !Object.keys(parentStep._parentPrimitives ?? parentStep.parentPrimitives ?? {}).includes( flowInstance.id ))){
                                throw `mismatch on segment origin ${parentStep.id} not a child of flowInstance ${flowInstance.id}`                                
                            }
                            imp = parentStep
                        }else{
                            throw "Need to move to segment origin to get flow step?"
                        }
                    }
                    if( imp.type === "category" ){
                        console.log(`-- Got category ${imp.id} / ${imp.plainId} for ${rel} - checking parent`)
                        const parent = (await fetchImports( [originId] ))[0]
                        if( parent ){
                            console.log(`-- Got parent of category  = ${parent.id} / ${parent.plainId}`)
                            if( !parent.flowElement && !Object.values(parent._parentPrimitives ?? parent.parentPrimitives ?? {}).find(d=>d.includes("primitives.config"))){
                                console.log(`--- Parent is not in a flow, can proceed`)
                                return false
                            }
                            imp = parent
                        }else{
                            console.log(`-- Couldnt get parent`)
                        }

                    }
                    if( imp.id !== flowInstance.id){
                        if( !Object.keys(imp._parentPrimitives ?? imp.parentPrimitives ?? {}).includes(flowInstance.id) ){
                            console.log(`-- ${imp.id} / ${imp.plainId} not in this flow instance - checking ancestors`)
                            if( functions.relevantInstanceForFlowChain ){
                                const chainResult = await functions.relevantInstanceForFlowChain( [imp], [flowInstance.id])
                                const resolvedAncestor = chainResult.some(Boolean)
                                if( !resolvedAncestor ){
                                    console.log(`${imp.id} / ${imp.plainId} is not linked to flow instance ${flowInstance.id} / ${flowInstance.plainId} for ${step.id} / ${step.plainId}`)
                                    can = false
                                    continue
                                }else{
                                    inAncestor = true
                                    console.log(`-- found in ancestor chain`)
                                }
                            }
                        }
                    }
                    const isSameFlow       = imp.id === flowInstance.id;
                    const hasValidStart    = flowStarted !== undefined;
                    const otherFlowStarted = imp.processing?.flow?.started;
                    const isComplete       = imp.processing?.flow?.status === "complete" || imp.processing?.flow?.status === "error_ignore" ;

                    const timingOk = !inAncestor ? otherFlowStarted <= flowStarted : otherFlowStarted <= flowStarted;
                    const importPrimValid = isSameFlow || (hasValidStart && isComplete && timingOk);

                    //console.log(`Checking status of ${rel} step ${imp.id} / ${imp.plainId} = ${importPrimValid} for ${step.id} / ${step.plainId}`)
                    can = can && importPrimValid
                }
            }
            return !can
        }
        const waitImports = await checkOutstandingSource( "imports" )
        const waitInputs = await checkOutstandingSource( "inputs" )
        let waitAxis = false
        if( step.type === "view" || step.type === "query" ){
            const waitAxisCol = await checkOutstandingSource( "axis.column" )
            const waitAxisRow = await checkOutstandingSource( "axis.row" )
            waitAxis = waitAxisCol || waitAxisRow
        }
        if( waitImports || waitInputs || waitAxis){
            canReason = ""
            if( waitImports){
                canReason = "data_"
            }
            if( waitInputs){
                canReason += "inputs_"
            }
            if( waitAxis){
                canReason += "axis_"
            }
            canReason += "not_ready"
            can = false
        }
        if( can ){
            const haltActive = Object.values(stepState).some(st=>st?.flowControl?.evaluated && st.flowControl?.haltFlow);
            if( haltActive && step.type !== "flowcontrol"){
                can = false;
                canReason = "flow_control_halt";
            }
        }
        return {can, canReason}
    },doFilter: ({resolvedFilterType, filter, setToCheck, lookups, check, scope, includeNulls, isRange}, fns)=>{
            const invert = filter.invert ?? false
            const dateFilter = resolvedFilterType === "parameter" && (isTopLevelPrimitiveDateField(filter.param) || filter.param === "date" || filter.param.endsWith("date"))
            if( dateFilter ){
                check = check.map((entry)=>{
                    if( entry && typeof entry === "object" && !Array.isArray(entry) ){
                        const next = {...entry}
                        if( next.gte !== undefined ){
                            next.gte = normalizeDateFilterValue(next.gte)
                        }
                        if( next.lte !== undefined ){
                            next.lte = normalizeDateFilterValue(next.lte)
                        }
                        return next
                    }
                    return normalizeDateFilterValue(entry)
                })
            }
            const temp = []
            const hasScope = Array.isArray(scope) && scope.length > 0;
            const hScope   = hasScope ? new Set(scope) : null;

            let doCheck
            if( isRange ){
                let c = check.find(Boolean)
                if( !c || c.hasOwnProperty("min_value") || c.hasOwnProperty("max_value") ){
                    doCheck =  (d)=>{
                        return check.find(c=>{
                            if( !c || (c.min_value === null && c.max_value === null) ){
                                return (d === null || d === undefined)
                            }
                            return (d >= (c.min_value ?? -Infinity) && d <= (c.max_value ?? Infinity))
                        }) !== undefined
                    }
                }else if( c.hasOwnProperty("gte") ){
                    doCheck =  (d)=>{
                        return check.find(c=>{
                            return d >= c.gte
                        }) !== undefined
                    }
                }else if( c.hasOwnProperty("lte") ){
                    doCheck =  (d)=>{
                        return check.find(c=>{
                            return d <= c.lte
                        }) !== undefined
                    }
                }
            }else if( resolvedFilterType === "segment_filter"){
                doCheck = (d)=>{
                    return fns.findFilterMatches(d, check)
                }
            }else{
                const checkSet = new Set(check);
                doCheck = (d) => {
                    if (Array.isArray(d)) {
                        for (const v of d) {
                            if (checkSet.has(v)) return true;
                        }
                        return false;
                    }
                    if (d === null) {
                        return checkSet.has(undefined);
                    }
                    return checkSet.has(d);
                };
            }

            let idx = 0
            for(const d of setToCheck){
                let data
                if( resolvedFilterType === "title"){
                    data = lookups[idx].map(d=>d.title)
                }
                else if( resolvedFilterType === "parameter"){
                    const parts = filter.param.split(".");
                    if( isTopLevelPrimitiveDateField(filter.param) ){
                        data = lookups[idx].flatMap((d)=>primitiveTimestampFallback(d, filter.param))
                    }else if( parts.length > 1){
                        data = lookups[idx].flatMap(d=>{
                            const r = PrimitiveConfig.decodeParameterFromParts(d.referenceParameters, parts) 
                            return Array.isArray(r) && r.length === 0 ? undefined : r
                        })
                    }else{
                        data = lookups[idx].flatMap(d=>{
                            const r = d.referenceParameters[parts[0]]
                            return Array.isArray(r) && r.length === 0 ? undefined : r
                        })
                    }

                    if( dateFilter ){
                        data = data.map((value)=>normalizeDateFilterValue(value))
                    }
                    //data = data.flatMap(d=>Array.isArray(d) && d.length === 0 ? undefined : d)
                }else if( resolvedFilterType === "type"){
                    data = lookups[idx].map(d=>d.referenceId)
                }else if( resolvedFilterType === "not_category_level1"){
                    const parentIds = lookups[idx].map(d=>fns.parentIds(d)).flat()
                    data = scope.filter(d=>parentIds.includes(d))
                }else if( resolvedFilterType === "segment_filter"){
                    data = lookups[idx].map(d=>{
                        return d.referenceParameters?.importConfig?.flatMap(d=>d.filters).map(d=>{
                            if(d.type === "parameter" || d.type === "title" || d.type === "structured_summary"){
                                delete d["sourcePrimId"]
                            }
                            return d
                        })
                    })                        
                }else if( resolvedFilterType === "structured_summary"){
                    const heading = filter.param ?? filter.parameter
                    const normalizedHeading = (heading ?? "").trim().toLowerCase().replace(/[^\w\s]/g, "")
                    data = lookups[idx].map(d=>{
                        const ss = d.referenceParameters?.structured_summary
                        if(!Array.isArray(ss)) return undefined
                        const match = ss.find(e=>{
                            const entryHeading = (e?.heading ?? "").trim().toLowerCase().replace(/[^\w\s]/g, "")
                            return entryHeading === normalizedHeading
                        })
                        const content = match?.content
                        return (typeof content === "string") ? content.trim() : content
                    })
                }else if( resolvedFilterType === "parent"){
                    const seen = new Set();
                    data = []
                    for (const obj of lookups[idx]) {
                        const ids = filter.sourcePrimId ? fns.parentIds(obj) : [obj.id];
                        for (const id of ids) {
                            if ((!hasScope || hScope.has(id)) && !seen.has(id)) {
                                seen.add(id);
                                data.push(id);
                            }
                        }
                    }
                        
                }

                if( invert ){
                    if( (data.length === 0 && !includeNulls) || !data.reduce((a,d)=>a && doCheck(d), true) ){
                        temp.push( d )
                    }
                }else{
                    if((data.length === 0 && includeNulls) || data.reduce((a,d)=>a || doCheck(d), false) ){
                        temp.push( d )
                    }
                }
                idx++
            }
            return temp
    }
}

function structuredSummaryChildren(node){
    if(Array.isArray(node)){
        return node.filter(Boolean)
    }
    if(node && typeof node === "object"){
        return Object.values(node).filter(Boolean)
    }
    return []
}

// Breadth-first search through structured summary hierarchy.
export function findStructuredSummarySection(structuredSummary, predicate){
    if(!structuredSummary || typeof predicate !== "function"){
        return null
    }
    const queue = structuredSummaryChildren(structuredSummary)
    while(queue.length){
        const current = queue.shift()
        if(!current || typeof current !== "object"){
            continue
        }
        if(predicate(current)){
            return current
        }
        queue.push(...structuredSummaryChildren(current.subsections))
    }
    return null
}

export function flattenStructuredResponse(nodeResult, nodeStruct, allowHeadings = true, headerLevel = 0, first = true) {
    let out = ""

    for(const d in nodeResult){
        const nextR = nodeResult?.[d]
        if( nextR?.heading){
            if( allowHeadings ){
                const h_level = Math.min(headerLevel + 1, 6)
                out += `${"#".repeat(h_level)} ${nextR.heading}\n`
            }else{
                //out += `- **${nextR.heading}:** `
            }
        }
        if( nextR?.content ){
            if( nextR?.type?.match(/list/) || nextR.content.trim().startsWith("- ")){
                let asArray = nextR.content ?? []

                if( typeof(nextR.content) === "string"){
                    asArray = nextR.content.trim().split(/\n/)
                }
                
                out += asArray.map(d=>{
                    if( typeof(d) === "string"){
                        d = d.trim()
                        if(!d.startsWith("- ")){
                            d = "- " + d
                        }
                    }
                    return d
                }).join("\n") + "\n"
            }else{
                let val = nextR.content
                /*if(val.trim().startsWith("- ")){
                    val = val.slice(1).trim()
                }*/
                out += `${val}\n`
            }
        }
        if( nextR?.subsections){
            out += flattenStructuredResponse(nextR?.subsections, nextR?.subsections, allowHeadings, headerLevel + 1, false) + "\n"
        }
    }
    return first ? out.trim() : out
}


PrimitiveConfig.flattenStructuredResponse = flattenStructuredResponse
PrimitiveConfig.findStructuredSummarySection = findStructuredSummarySection

export default PrimitiveConfig
